import { isCatalogLeafPartId } from '@misumi/shared'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { FolderOutlined, LeftOutlined } from '../../components/icons'
import { Button } from '../../components/ui/Button'
import { Checkbox } from '../../components/ui/Checkbox'
import { EmptyState } from '../../components/ui/EmptyState'
import { ProgressPanel } from '../../components/ui/ProgressPanel'
import { useToast } from '../../components/ui/ToastProvider'
import { useRfqBundle } from '../../lib/rfq/rfq-bundle-context'
import type { SpecSelection } from '../../lib/rfq/rfq-bundle-context'
import { useUploadBatch } from './UploadFlowProvider'
import { FileToResolveRow } from './components/FileToResolveRow'
import { IdentifiedPartRow } from './components/IdentifiedPartRow'
import { MatchedPartRow } from './components/MatchedPartRow'
import { StagingBomPartsTree } from './components/StagingBomPartsTree'
import { StagingSelectionBand } from './components/StagingSelectionBand'
import { StagingSummaryCard } from './components/StagingSummaryCard'
import { StagingToolbar } from './components/StagingToolbar'
import { DEMO_DRAWING_GENERATE_DELAY_MS, buildSessionDrawingPool } from './session-drawing-pool'
import { STAGING_SECTION_LABELS, formatBomLineLabel } from './staging-bom-accessible-names'
import {
  buildUnifiedBomRows,
  collectSelectablePartIds,
  countPartRows,
  findNodeIdsForPartId,
  findRemovalFocusFallback,
} from './staging-bom-tree-view-model'
import type { BomTreeLeafNode, FocusFallbackTarget } from './staging-bom-tree-view-model'
import { deriveStagingCta, isIdentifiedConfirmable, isMatchedConfirmable } from './staging-cta'
import {
  filterByPartName,
  filterFilesToResolveByQuery,
  filterUnifiedBomRows,
  isQueryActive,
} from './staging-part-search'
import { projectLinkWarningMessage } from './staging-project-link'
import { indexAnalysisSignalRows, lookupAnalysisSignal } from './staging-types'
import type {
  ConfirmProjectLinkOutcome,
  StagedConfiguration,
  StagingBomNode,
  StagingTriage,
} from './staging-types'
import { useDebouncedValue } from './use-debounced-value'
import { defaultStagingService } from './useStaging'
import type { StagingService } from './useStaging'
import { useStaging } from './useStaging'
import { TRANSFER_FAILED_MESSAGE, wasUploadInterrupted } from './useUploads'

/** ADR-0155 WARNING E (Gate-5 re-review) — the delay window for
 * `debouncedSearchDisclosureMessage` below. 300ms is inside the ADR's stated
 * 250–400ms range and matches this codebase's existing search-debounce
 * convention (`SEARCH_DEBOUNCE_MS` in `use-add-parts-search.ts`/
 * `AddPartsModal.tsx`). Deliberately NOT applied to `searchQuery` itself or
 * anything the filtering reads — only the announcement. */
const SEARCH_DISCLOSURE_DEBOUNCE_MS = 300

export interface StagingPageProps {
  /** Injectable for tests / future BFF-backed swap. */
  readonly service?: StagingService
}

/** Pulled out to a constant purely so the JSX line that uses it stays under
 * the 100-char formatter width without wrapping onto multiple lines — a
 * `biome-ignore` comment directly above a multi-line JSX opening tag does
 * not reliably suppress a diagnostic reported on an inner attribute line. */
const ANALYZING_LABEL = 'Upload analysis progress'

/**
 * Where the REAL, byte-weighted upload measurement stops and the simulated
 * analysis ramp begins, as a percentage of the one bar both feed.
 *
 * UID-583 / Gate-5 W1 — the bar spans the WHOLE operation (transfer, then
 * analysis), so the transfer cannot be allowed to reach 100%: it would freeze
 * there for the entire completion window and then jump backwards when the ramp
 * took over. 80 keeps the transfer — the long, genuinely-measured part — as
 * most of the bar, and leaves the analysis phase somewhere to move.
 *
 * The measurement itself is NOT distorted by this: `batch.pct` is still the
 * true fraction of bytes transferred, and it is scaled at the point of DISPLAY
 * because what the bar reports is "how far through getting your files ready",
 * not "how many bytes moved".
 */
const UPLOAD_PHASE_CEILING = 80

/**
 * UID-553 — map a confirmed part's staging {@link StagedConfiguration} to the
 * RFQ bundle's `specSeed` (a `SpecSelection`), so the RFQ compose screen's
 * Configure step is PRE-POPULATED for a configurable/custom line. Only the
 * spec-relevant fields cross over: `process`/`material`/`finish` directly, the
 * single `inspection` as the `inspections[]` the RFQ shape uses, and
 * `certificates` when present. `quantity` does NOT belong here — it rides
 * `RfqBundleItem.quantity` into `quantityOverrides` on its own.
 *
 * `process` is a plain string on `StagedConfiguration` but a `PartProcess` enum
 * on `RfqLineSpecification`; the server only ever seeds a real `PartProcess`
 * (via `tryNormalizeProcess`), so the value is sound and the cast is the seam
 * where the wider string is narrowed to the closed set the RFQ shape declares.
 */
function toSpecSeed(config: StagedConfiguration): SpecSelection {
  const specification: SpecSelection['specification'] = {
    process: config.process as SpecSelection['specification']['process'],
    material: config.material,
    finish: config.finish,
    ...(config.inspection ? { inspections: [config.inspection] } : {}),
    ...(config.certificates && config.certificates.length > 0
      ? { certificates: [...config.certificates] }
      : {}),
  }
  return { specification }
}

// UID-402 — `isIdentifiedReady`/`isMatchedReady` are gone with the status
// filter they existed to drive. Per-row readiness is still visible (each row's
// own "Configuration status" / match-quality cell), and session-wide
// priceability now lives in `staging-cta.ts` as the CTA's own predicate. The
// status filter stays banned even after ADR-0155 (D1) — this paragraph is
// unaffected by that ADR.
//
// ADR-0155 REINSTATES the search box ADR-0124 D6 deleted (`matchesQuery`,
// `query` state, `StagingToolbar`) — search only, page-wide, name-only
// haystack (D2), filtering every section uniformly including
// needs-resolution (D6(a)). See `staging-part-search.ts` for the matcher/
// pruner and this file's `searchQuery` state below for the wiring. The CTA,
// every count pill, and `selectableRowIds` still read the UNFILTERED triage
// arrays (D3/D6(b)) — see the `displayed*`/`searchFiltered*` naming split
// just below.

/**
 * Staging triage (UID-184, `Fictiv Platform.dc.html:2511-2955`) — the core
 * CAD-intake review screen: chrome/header (S-A), a unified 6-tile summary strip
 * carrying the one primary CTA (S-C), a sticky selection/action bar (S-G), and
 * three top-level buckets keyed off `StagingTriage`'s own arrays — Needs
 * resolution FIRST (S-F), then Identified parts (S-D) and Matched to catalog
 * (S-E). Builds against the live three-bucket model, NOT the source's dead
 * confirm/reject row-tint path (interactions.md §4 / staging-types.ts's doc
 * comment).
 *
 * UID-402 (ADR-0124) makes this page REVIEW/RESOLVE ONLY: no configuration
 * entry point, one session-wide priceability-derived CTA gated on resolution,
 * orange scoped to "needs resolution", and **no status/triage filter or
 * flat⇄tree toggle** — those stay banned. ADR-0155 (2026-09-04, human
 * authorization) REINSTATES the S-B search box ADR-0124 D6 removed — search
 * only, one query, page-wide, name-only haystack — while re-asserting D6's
 * *filter* clause. The row set may now be narrowed by that one affordance,
 * but only visibly (ADR-0155 D4): every staged row renders unless the user is
 * actively typing a query, and the CTA/counts/selection never move because of
 * it (D3/D6(b)).
 *
 * UID-254 (2026-08-24) reverses ADR-0079's two-state "Needs attention" /
 * "Ready to add" IA back to these three flat buckets — see the ADR-0079
 * "UID-254 amendment" and `claude-feature-notes/
 * 2026-08-24-uid-254-triage-three-buckets-summary-strip.md`. Per that note's
 * architect ruling (Option C), the ADR-0086 BOM-hierarchy tree is parked,
 * not wired in here — see `staging-bom-tree-view-model.ts`'s tracking
 * comment for disposition status.
 */
/**
 * Gate-5 **C-D** — an `ungrouped` leaf that NO verb can address, and so must
 * not be selectable.
 *
 * Every `ungrouped` leaf is synthesized by the coverage diff with a
 * client-only `nodeId` and no server row behind it. On the FILE-BACKED arm that
 * is harmless — `removeStagedPart` is `partId`-scoped and the
 * `intake.staged_file` row genuinely exists. On the CATALOG arm nothing is
 * addressable: `removeBomNode` 404s on a client-only id, `removeStagedPart`
 * cannot reach a catalog leaf at all (migration 1013's arm mutex), and a
 * confirm has no BOM position to stamp (ADR-0162 D2.4). Selecting one could
 * only ever produce a failure that advises a retry which cannot succeed.
 *
 * Kept next to the row's own `isUnactionableCatalogRow` guard, which disables
 * the per-row checkbox. BOTH are needed and neither substitutes for the other:
 * this one governs select-all and the shift-click range math, the row's governs
 * the control the user actually clicks. A previous attempt fixed only this half
 * and changed nothing on screen.
 */
function isUnactionableUngroupedLeaf(leaf: BomTreeLeafNode): boolean {
  // `partId` is typed `string | null` on the leaf generally, though every
  // `ungrouped` leaf's is non-null by construction (minted from `part.partId`).
  // Narrowed rather than asserted: a null `partId` is not a catalog leaf, which
  // is the honest answer for this predicate either way.
  return leaf.partId !== null && isCatalogLeafPartId(leaf.partId)
}

export function StagingPage({ service = defaultStagingService }: StagingPageProps = {}) {
  const { sessionId = '' } = useParams<{ sessionId: string }>()
  const location = useLocation()
  // UID-509 follow-up — `justUploaded` (set by `useUploads.ts`'s `navigate`
  // call right after an upload) applies a minimum-display floor to the
  // "Analyzing your files" loading state, so it's visible RUNNING BEHIND
  // the "Add these files to a project" modal even on an unthrottled local
  // connection. Arriving here any other way (a bookmarked/shared session
  // link, `refetch()`) carries no such state and gets the default,
  // unthrottled behavior — see `useStaging`'s own doc comment for why this
  // is opt-in per call site rather than a hook-wide floor.
  const justUploaded = Boolean((location.state as { justUploaded?: boolean } | null)?.justUploaded)
  /**
   * UID-583 (ADR-0173 D3) — the batch that created this session, if this
   * browser tab is the one that started it.
   *
   * `null` for every other way of arriving here (a resumed session card, a
   * bookmarked or shared link, a reload), and those paths keep their exact
   * pre-UID-583 behaviour: no hold, and `justUploaded`'s floor alone. The
   * hold is not a guess about timing — it is the flow telling this page
   * whether the files it is about to describe have actually been uploaded
   * yet.
   */
  const batch = useUploadBatch(sessionId)
  /**
   * Gate-5 C2 (scenario A) — this tab started an upload for this session and
   * never finished it, and the in-memory record is gone (a reload).
   *
   * Read ONCE via a lazy initializer, not on every render: it is a fact about
   * how this page was arrived at, and re-reading it would make the notice
   * flicker if the marker is cleared while the page is mounted. A live
   * `batch` always wins — the marker is only consulted for its absence.
   */
  const [uploadWasInterrupted] = useState(() => wasUploadInterrupted(sessionId))
  /**
   * Bytes are actually moving. The window in which the panel shows REAL,
   * byte-weighted numbers (D6).
   *
   * Gate-5 W1 — `'uploading'` ONLY, deliberately narrower than the hold
   * below. `'completing'` is the BOM-hierarchy capture: every byte has
   * landed, so there is no transfer left to measure and pinning the bar to a
   * real 100% for that whole window (up to `COMPLETE_SESSION_TIMEOUT_MS`)
   * froze it, then jumped backwards. Completion belongs to the analysis
   * phase, which is what the simulated ramp is for.
   */
  const transferring = batch !== null && batch.phase === 'uploading'
  /** Whether an upload phase preceded this analysis phase — so the ramp knows
   * whether to start from the upload ceiling or from zero (Gate-5 W1). */
  const hadTransfer = batch !== null
  /**
   * Whether the triage read must stay held (ADR-0173 D3).
   *
   * WIDER than `transferring` by two phases, and the difference is
   * load-bearing: `'failed'` means not one file's bytes landed, so the session
   * is EMPTY and its triage has nothing in it worth reading. Releasing the
   * read there would fire a request whose answer is discarded — the failure
   * arm below renders instead of any triage state — and, on a flaky
   * connection, would follow one failure with a second, unrelated error. The
   * hold is safe precisely because that arm is unconditional: it cannot
   * strand the user on a spinner.
   */
  const holdTriageRead = batch !== null && batch.phase !== 'done'
  // 2000ms — inside the design's own `runAnalysis()` range (~1.5-2.5s,
  // `Fictiv Platform.dc.html:8214-8219`).
  const { state, settling, refetch, applyMutation } = useStaging(
    sessionId,
    service,
    justUploaded ? 2000 : 0,
    holdTriageRead,
  )
  const navigate = useNavigate()

  /**
   * A client-side SIMULATED progress percentage for the "Analyzing your
   * files" state — deliberately distinct from the earlier, rejected
   * `analyzingPct = triage?.analysisProgress ?? 8` (see this file's own
   * history): that number was STATIC, always stuck at 8%, because the real
   * backend has no progress-reporting seam (ADR-0134 R2, still true) and it
   * never moved — a static, misleading number, correctly torn out.
   *
   * This is different in the one way that matters: it ANIMATES, openly
   * simulated, ticking toward ~97% while genuinely waiting and snapping to
   * 100% the moment `settling` reports the real request is actually done
   * (only the `minLoadingMs` floor is left) — the same posture the design's
   * OWN `runAnalysis()` takes (`Fictiv Platform.dc.html:8214-8219`): its
   * `progress` is `Math.random()*9+3` per 180ms tick, not real backend data
   * either. Never claims to be authentic backend progress; only ever
   * rendered while `state.status === 'loading'`, so it never contradicts
   * `state.status === 'ready'`.
   */
  /**
   * The ONE announcement the upload phase makes, and it is made ONCE.
   *
   * Gate-5 C3 + W2 — the deleted `UploadFileList` had a deliberate one-shot
   * `<output aria-live="polite">` that stayed empty for the whole transfer and
   * was given content only when every file reached a terminal state, precisely
   * so a screen reader was not read a number on every XHR progress tick. The
   * feature note claimed that announcement "moves onto the analysing panel";
   * it did not — it was deleted, and nothing announced batch completion at
   * all. This restores it, with the same copy, on this page.
   *
   * It canNOT go through the page-level `liveAnnouncement` region (ADR-0157):
   * that region is rendered in the LOADED branch, and this fires while the
   * page is still `'loading'`, so it would have no mounted region to speak
   * through. It is instead rendered inside the loading branch, mounted empty
   * from the first render (ADR-0124 W8: a live region must exist before its
   * content changes) and given content exactly once.
   */
  const [uploadAnnouncement, setUploadAnnouncement] = useState('')
  const [analyzingPct, setAnalyzingPct] = useState(0)
  useEffect(() => {
    if (state.status !== 'loading') return
    // UID-583 (ADR-0173 D6) — while the bytes are actually moving there is a
    // REAL number to show (`batch.pct`, byte-weighted, scaled into the first
    // `UPLOAD_PHASE_CEILING`% of the bar), so the simulated ramp stands down
    // rather than competing with it: the interval is not merely ignored but
    // not STARTED, so the two never both drive the bar.
    if (transferring) return
    if (settling) {
      setAnalyzingPct(100)
      return
    }
    // Gate-5 W1 — SEED AT THE UPLOAD PHASE'S CEILING when a batch preceded
    // this, never at 0. Seeding at 0 made the bar read 100% for the whole
    // completion window and then JUMP BACKWARDS to ~6% when the ramp took
    // over — the "static/stuck misleading number" this panel's own history
    // records tearing out, reintroduced as a moving one. `Math.max` because a
    // same-session refetch must never rewind a bar that has already advanced.
    setAnalyzingPct((current) => Math.max(current, hadTransfer ? UPLOAD_PHASE_CEILING : 0))
    const id = setInterval(() => {
      setAnalyzingPct((current) => Math.min(97, current + Math.random() * 9 + 3))
    }, 180)
    return () => clearInterval(id)
  }, [state.status, settling, transferring, hadTransfer])

  /**
   * Announce the transfer's completion once, when it stops being in flight.
   *
   * Keyed off `transferring` going false with a batch present, NOT off a
   * count reaching a total: `settledCount` counts TERMINAL rows (uploaded or
   * failed), so the two agree, but the phase is the authoritative signal and
   * it cannot be reached twice for one batch. Copy is the deleted
   * component's, verbatim, including the failure arm.
   */
  useEffect(() => {
    if (!hadTransfer || transferring || batch === null) return
    if (batch.phase !== 'completing' && batch.phase !== 'done') return
    const ready = batch.settledCount - batch.failedFiles.length
    setUploadAnnouncement((current) =>
      current !== ''
        ? current
        : batch.failedFiles.length > 0
          ? `Upload complete. ${ready} of ${batch.total} file${
              batch.total === 1 ? '' : 's'
            } ready, ${batch.failedFiles.length} failed.`
          : `Upload complete. ${ready} file${ready === 1 ? '' : 's'} ready.`,
    )
    // Same fresh-object hazard as the effect below: `batch` is in these deps,
    // so this re-runs on every render. It is safe ONLY because the setter
    // above returns `current` unchanged once set, which React treats as a
    // no-op — the latch is in the state update, not in the dependency list.
  }, [hadTransfer, transferring, batch])

  /**
   * Gate-5 W2(b) — the failure notice's content is announced through the
   * page's ONE live region (ADR-0157), not by making the notice itself a
   * second one.
   *
   * RE-REVIEW FIX. The first version of this effect claimed to "fire once" and
   * did the opposite, in a way that broke the very rule it was written to
   * honour: `batchFor` returns a FRESH OBJECT on every call, so `batch` in the
   * dependency array made this re-run on every render — and every re-run
   * re-set `liveAnnouncement`, CLOBBERING whatever the user's last action had
   * put there ("Dismissed X. Your stated quantity is kept."). A shared
   * announcement region has exactly one writer at a time; stealing it back on
   * the next render is worse than the second live region this was replacing.
   *
   * So: a ref latch, and PRIMITIVE dependencies only. `hasBatch` rather than
   * `batch`, because the object's identity is meaningless here — only whether
   * one exists.
   */
  const failureNoticeCount =
    batch === null ? 0 : batch.failedFiles.length + batch.refusedFiles.length
  const hasBatch = batch !== null
  const failureAnnouncedRef = useRef(false)
  useEffect(() => {
    if (state.status !== 'ready' || failureAnnouncedRef.current) return
    if (uploadWasInterrupted && !hasBatch) {
      failureAnnouncedRef.current = true
      setLiveAnnouncement(
        'This upload was interrupted, so the assembly structure is missing. Details are above the parts list.',
      )
      return
    }
    if (failureNoticeCount === 0) return
    failureAnnouncedRef.current = true
    setLiveAnnouncement(
      `${failureNoticeCount} file${
        failureNoticeCount === 1 ? '' : 's'
      } from this upload could not be added. Details are above the parts list.`,
    )
  }, [failureNoticeCount, state.status, uploadWasInterrupted, hasBatch])

  /**
   * ADR-0130 (UID-425 Bug 2) — ONE unified selection map across identified +
   * matched rows ("Shape B" ruling), mirroring the design's single
   * `stagedSel` (`Fictiv Platform.dc.html`'s `data-sel-key="stagedSel"`).
   * This REPLACES the old `selectedMatched` (matched-only). Restored per
   * ADR-0124 D1's checkbox clause being reversed — see IdentifiedPartRow's
   * doc comment — but D1's configuration ban and D2's CTA independence are
   * UNCHANGED: this set feeds ONLY the per-section `StagingSelectionBand`s'
   * "Delete selected" (the sticky selection/action card it also used to feed
   * — "Confirm & add to library"/"Add to BOM" — is removed as not-in-design),
   * never `deriveStagingCta`.
   */
  /** UID-583 (ADR-0173 D5) — dismissal of the "these files didn't upload"
   * notice. Page-local and deliberately NOT persisted: the notice reports a
   * transport failure in THIS batch, and a reload has no batch to report on
   * (`batchFor` returns `null`), so there is no state worth surviving. */
  const [uploadFailuresDismissed, setUploadFailuresDismissed] = useState(false)
  const [selectedStaged, setSelectedStaged] = useState<ReadonlySet<string>>(new Set())
  /** ADR-0130 — the last row a checkbox click landed on, for shift-click
   * range selection (design: `data-sel-key`/`data-sel-id` wiring). */
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null)
  const [expandedMatchedId, setExpandedMatchedId] = useState<string | null>(null)
  /**
   * PM ruling (2026-09-01) — "Delete selected" moved out of the sticky action
   * bar into a per-section `StagingSelectionBand`, following the design's own
   * selection band (`Fictiv Platform.dc.html:3136-3143`), re-read closely: it
   * is welded under each SECTION header, not the action bar. Each section
   * gets its OWN busy/error pair — sharing one across two independently
   * actionable sections would repeat the exact W5 cross-talk this whole
   * ticket keeps fixing (deleting in one section spinning/erroring the
   * OTHER section's band).
   */
  const [identifiedBulkDeleting, setIdentifiedBulkDeleting] = useState(false)
  const [identifiedBulkDeleteError, setIdentifiedBulkDeleteError] = useState<string | undefined>(
    undefined,
  )
  const [matchedBulkDeleting, setMatchedBulkDeleting] = useState(false)
  const [matchedBulkDeleteError, setMatchedBulkDeleteError] = useState<string | undefined>(
    undefined,
  )
  const { showToast } = useToast()

  /**
   * Gate-5 W5 — the "Review in library" hand-off owns its OWN busy/error
   * state, distinct from the RFQ hand-off's below.
   *
   *   `handoffBusy`    — that write is in flight
   *   `handoffError`   — it failed, reported beside the hand-off button
   */
  const [handoffBusy, setHandoffBusy] = useState(false)
  const [handoffError, setHandoffError] = useState<string | undefined>(undefined)

  /**
   * ADR-0130 D3 (UID-425 Bug 3) — the Triage -> RFQ hand-off's OWN
   * busy/error state. Gate-5 W5's ruling applies verbatim: this must NOT
   * share flags with `handoffBusy`/`handoffError` above (which belong to
   * "Review in library") — sharing them would leak spinners/errors across
   * two genuinely independent actions.
   *   `rfqHandoffBusy`   — that write is in flight
   *   `rfqHandoffError`  — it failed (or there was nothing to confirm),
   *                        reported beside the primary CTA
   */
  const [rfqHandoffBusy, setRfqHandoffBusy] = useState(false)
  const [rfqHandoffError, setRfqHandoffError] = useState<string | undefined>(undefined)
  const rfqBundle = useRfqBundle()

  // QA Bug 1 (HIGH): every row-level mutation (ignore/undo/swap/keep-uploaded/
  // associate-drawing/update-quantity/resolveFile) used to call
  // `applyMutation(...)` fire-and-forget — a rejection was genuinely
  // unhandled and surfaced nothing to the user. `runMutation` below is the
  // one place those calls now funnel through: awaited, caught, and reported
  // here via a page-level `role="alert"` region.
  const [mutationError, setMutationError] = useState<string | undefined>(undefined)

  /**
   * ADR-0157 — a per-row Remove (catalog leaf) or Dismiss (unresolved leaf)
   * announces its RESULT through this ONE page-level `aria-live="polite"`
   * region, never a per-row one (the human's spec: multiplicity belongs in
   * the confirmation, not only the button name). A fresh string on every
   * announcement (even a repeat) is required for most screen readers to
   * re-announce identical text — plain state is enough here since the
   * region is always re-rendered with new copy, never the exact same
   * string twice in a row for two different actions.
   */
  const [liveAnnouncement, setLiveAnnouncement] = useState('')

  /**
   * ADR-0157 — the "also flagged" focus-management gap: a hard Remove drops
   * its row from the DOM entirely (Dismiss never does — its button just
   * swaps to Undo in place, so focus never leaves the control it started
   * on). `pendingFocusTarget` is set the instant a Remove SUCCEEDS (the
   * fallback itself is computed BEFORE the mutation, off the PRE-removal
   * tree, since the removed node's siblings are what `findRemovalFocusFallback`
   * needs and they're unaffected by its own removal); the effect below then
   * runs AFTER the triage update has already committed to the DOM (a
   * separate render, since `pendingFocusTarget` is set only once
   * `runMutation`'s promise has resolved) and moves focus there — next
   * leaf's Remove → previous leaf's Remove → parent assembly's checkbox →
   * the section heading (`tabIndex={-1}`, never left to fall through to
   * `<body>`).
   */
  const [pendingFocusTarget, setPendingFocusTarget] = useState<FocusFallbackTarget | null>(null)
  const bomSectionRef = useRef<HTMLElement>(null)
  const bomSectionHeadingRef = useRef<HTMLHeadingElement>(null)

  useEffect(() => {
    if (!pendingFocusTarget) return
    const root = bomSectionRef.current
    const focusHeading = () => bomSectionHeadingRef.current?.focus()
    if (!root || pendingFocusTarget.kind === 'section-heading') {
      focusHeading()
    } else if (pendingFocusTarget.kind === 'leaf-remove') {
      const el = root.querySelector<HTMLElement>(
        `[data-node-id="${pendingFocusTarget.nodeId}"] .staging-row-remove`,
      )
      el ? el.focus() : focusHeading()
    } else {
      const el = root.querySelector<HTMLElement>(
        `[data-node-id="${pendingFocusTarget.nodeId}"] input[type="checkbox"]`,
      )
      el ? el.focus() : focusHeading()
    }
    setPendingFocusTarget(null)
  }, [pendingFocusTarget])

  const triage = state.status === 'ready' ? state.triage : null

  // UID-255 — index the "system is thinking" auto-quote-vs-manual signal by
  // row id ONCE here, so every row-rendering section/component below does an
  // O(1) lookup instead of re-deriving it. `undefined` (no `analysisSignal`
  // on this triage) flows straight through as "no tint/chip anywhere" — the
  // defensive fallback for the pre-UID-255 shape.
  const analysisSignalById = useMemo(
    () => indexAnalysisSignalRows(triage?.analysisSignal),
    [triage],
  )

  // ADR-0155 — the search box is back, but `displayed*` MUST NOT become the
  // filtered arrays (this ADR's Context correction, quoting `StagingPage.tsx`
  // verbatim: reassigning these three names to filtered arrays is a two-line
  // diff that type-checks and looks correct, and it is the single change that
  // silently breaks D3). `displayed*` stay exactly what they were under
  // ADR-0124 D6/D7 — the UNFILTERED triage arrays, read directly by every
  // count pill, the CTA, and `selectableRowIds`.
  const displayedIdentified = triage?.identified ?? []
  const displayedMatched = triage?.matched ?? []
  const displayedFiles = triage?.filesToResolve ?? []

  /**
   * ADR-0155 D4(5) — starts empty on every mount, never persisted, never
   * read from the URL. `StagingToolbar` is fully controlled by this state.
   */
  const [searchQuery, setSearchQuery] = useState('')

  /**
   * ADR-0155 D3's seam rule — three NEW, differently-named values, never a
   * reassignment of `displayed*`. Used ONLY for rendering rows; every count
   * pill, the CTA, and `selectableRowIds` keep reading `displayed*`
   * (unfiltered) below, unchanged by this ADR.
   */
  const searchFilteredIdentified = useMemo(
    () => filterByPartName(displayedIdentified, searchQuery),
    [displayedIdentified, searchQuery],
  )
  const searchFilteredMatched = useMemo(
    () => filterByPartName(displayedMatched, searchQuery),
    [displayedMatched, searchQuery],
  )
  /**
   * ADR-0155 D2/D6(a) — `FileToResolve` has no `name` field, so a non-empty
   * query narrows this to empty every time; the "Needs resolution" section
   * itself stays on screen regardless (D4(4) — its render gate below reads
   * `displayedFiles.length`, never this filtered array).
   */
  const searchFilteredFiles = useMemo(
    () => filterFilesToResolveByQuery(displayedFiles, searchQuery),
    [displayedFiles, searchQuery],
  )
  /** ADR-0155 D4(3) — drives each section's own distinct "nothing matches
   * this query" empty state, kept separate from the genuinely-empty-session
   * empty state (ADR-0124 D7's self-hiding sections, unaffected by this
   * ADR). SUGGESTION 10 (Gate-5 review) — reads `isQueryActive`, the single
   * shared source of truth for "is search on?", rather than its own
   * `.trim() !== ''` (which used to agree with the filter functions only by
   * coincidence — see that function's doc comment). */
  const hasActiveSearchQuery = isQueryActive(searchQuery)

  /**
   * ADR-0142 D2 — the unified "BOM parts" list REPLACES the Identified/
   * Matched buckets on a BOM session. `buildUnifiedBomRows` is
   * FLAT-ARRAY-DRIVEN, not hierarchy-driven (the gating condition of the
   * whole change): population is `triage.identified ?? [] ∪ triage.matched
   * ?? []` — the exact population the two legacy buckets render —
   * `bomHierarchy` supplies ordering/grouping only. A part no hierarchy leaf
   * references is never dropped; it is appended to `ungrouped`, still
   * selectable, still counted. See the view-model's own doc comment for why
   * this re-basing is required (ADR-0086 BUG-1, re-arriving through the
   * data-flow direction once the buckets are gone).
   */
  /**
   * UID-497 (T10) — the session's drawing pool, offered by every custom BOM
   * row's drawing menu (and, unchanged in content for it, the identified
   * rows'). Memoised on the triage because the pool walks the whole tree.
   */
  const drawingPool = useMemo(
    () => (triage == null ? [] : buildSessionDrawingPool(triage)),
    [triage],
  )

  const unifiedBomRows = useMemo(
    () =>
      buildUnifiedBomRows(
        triage?.bomHierarchy ?? [],
        triage?.identified ?? [],
        triage?.matched ?? [],
      ),
    [triage?.bomHierarchy, triage?.identified, triage?.matched],
  )

  /**
   * ADR-0142 D3/D12 — ONE derived boolean, used to gate BOTH the unified
   * section AND (on its negation) the legacy buckets — never two
   * disagreeing gates. Strengthened from the old `bomTree.length > 0`
   * (which counted top-level ASSEMBLY nodes) to "the hierarchy yields ≥1
   * PART row" (the prototype's own `some(r => r.isPart)`, `dc.html:9506`):
   * `unifiedBomRows.tree` only ever contains a `kind: 'assembly'` node when
   * it recursively contains ≥1 leaf (structural pruning — see
   * `pruneEmptyAssemblies`), so `tree.length > 0` already implies a part
   * row exists; `ungrouped.length > 0` covers the case where every part is
   * uncovered and the tree itself is empty.
   */
  const hasBomRows = unifiedBomRows.tree.length > 0 || unifiedBomRows.ungrouped.length > 0

  /** UX-spec §2 — the section's count pill is the leaf PART-ROW count
   * (grouped + ungrouped), never `unifiedBomRows.tree.length` (which counts
   * top-level ASSEMBLY nodes — today's fourth section's own bug). Reads the
   * UNFILTERED `unifiedBomRows` — ADR-0155 D3. */
  const bomPartRowCount = countPartRows(unifiedBomRows.tree) + unifiedBomRows.ungrouped.length

  /**
   * ADR-0155 D5 — the search-driven pruned COPY of `unifiedBomRows`, used
   * ONLY to render `StagingBomPartsTree`'s `tree`/`ungrouped` props below.
   * Every other consumer of `unifiedBomRows` (`hasBomRows`, `bomPartRowCount`,
   * `selectableRowIds`) keeps reading the unfiltered original — this is a
   * DIFFERENT, differently-named value, never a reassignment (D3's seam
   * rule, same as `searchFiltered*` above).
   */
  const searchFilteredUnifiedBomRows = useMemo(
    () => filterUnifiedBomRows(unifiedBomRows, searchQuery),
    [unifiedBomRows, searchQuery],
  )
  /** The BOM section's own "visible of total" pair for the page-level
   * "showing X of Y" indicator (D4(1)) — `bomVisiblePartRowCount` is the
   * FILTERED count, `bomPartRowCount` above stays the unfiltered one. */
  const bomVisiblePartRowCount =
    countPartRows(searchFilteredUnifiedBomRows.tree) + searchFilteredUnifiedBomRows.ungrouped.length

  /**
   * ADR-0130 / ADR-0142 D7(1) — every row id the unified checkbox can
   * select, in RENDER order (this ordering is also the range shift-click
   * walks). On a BOM session this is the unified list's own document order
   * — grouped leaves depth-first, then the uncovered/ungrouped rows — NOT
   * the identified-then-matched order the legacy buckets use; re-deriving
   * this in render order is required, not cosmetic, because shift-click
   * would otherwise select a range the user did not visually draw. On a
   * non-BOM session (buckets render) the ordering is unchanged: identified
   * first, then non-ignored matched (an ignored match's checkbox is
   * `disabled`, matching `MatchedPartRow`'s existing rule).
   */
  const selectableRowIds = useMemo<readonly string[]>(() => {
    if (hasBomRows) {
      // ADR-0142 D14.1(3) — one id per part, asserted duplicate-free by
      // `buildUnifiedBomRows`'s dedupe-by-`partId` pass (D13/D14.1(1)): every
      // `ungrouped` leaf's `partId` is non-null by CONSTRUCTION (minted from
      // `part.partId` at `staging-bom-tree-view-model.ts`) — the trailing
      // `.filter((id): id is string => id !== null)` below is therefore only
      // a TypeScript narrowing (the leaf type's `partId` field is typed
      // `string | null` generically), never a runtime safety net; it is
      // provably a no-op filter at runtime for this population, kept only so
      // this stays `readonly string[]` without a cast.

      return [
        ...unifiedBomRows.tree.flatMap(collectSelectablePartIds),
        ...unifiedBomRows.ungrouped
          .filter((leaf) => !(leaf.partKind === 'matched' && leaf.part.status === 'ignored'))
          // Gate-5 C-D — see `isUnactionableUngroupedLeaf`.
          .filter((leaf) => !isUnactionableUngroupedLeaf(leaf))
          .map((leaf) => leaf.partId)
          .filter((id): id is string => id !== null),
      ]
    }
    return [
      ...displayedIdentified.map((part) => part.partId),
      ...displayedMatched.filter((part) => part.status !== 'ignored').map((part) => part.partId),
    ]
  }, [hasBomRows, unifiedBomRows, displayedIdentified, displayedMatched])

  /**
   * ADR-0155 D6(b) — the SECOND, query-aware ordered list D6(b) requires for
   * range math, kept STRICTLY separate from `selectableRowIds` above (which
   * MUST stay unfiltered — it feeds the QA-Bug-2 pruning effect right below,
   * and narrowing it would irreversibly wipe ticked rows the instant a query
   * narrows them out, D6(b)'s own defect). This is the render-order id list
   * over the CURRENTLY VISIBLE rows only, built the same way
   * `selectableRowIds` is (mirrors its two branches exactly), just fed from
   * `searchFiltered*`/`searchFilteredUnifiedBomRows` instead of the
   * unfiltered originals. When no query is active, `filterUnifiedBomRows`/
   * `filterByPartName` are identity functions, so this list is REFERENCE-
   * EQUAL (same values, same order) to `selectableRowIds` and existing
   * shift-click behaviour is unchanged.
   *
   * ONLY consumer: `toggleStagedSelection`'s shift-click range walk below.
   * Do not widen its use — counts, the CTA, and the pruning effect all stay
   * on `selectableRowIds`.
   */
  const visibleSelectableRowIds = useMemo<readonly string[]>(() => {
    if (hasBomRows) {
      return [
        ...searchFilteredUnifiedBomRows.tree.flatMap(collectSelectablePartIds),
        ...searchFilteredUnifiedBomRows.ungrouped
          .filter((leaf) => !(leaf.partKind === 'matched' && leaf.part.status === 'ignored'))
          // Gate-5 C-D — must mirror `selectableRowIds` exactly, or a
          // shift-click range would span an id select-all cannot reach.
          .filter((leaf) => !isUnactionableUngroupedLeaf(leaf))
          .map((leaf) => leaf.partId)
          .filter((id): id is string => id !== null),
      ]
    }
    return [
      ...searchFilteredIdentified.map((part) => part.partId),
      ...searchFilteredMatched
        .filter((part) => part.status !== 'ignored')
        .map((part) => part.partId),
    ]
  }, [hasBomRows, searchFilteredUnifiedBomRows, searchFilteredIdentified, searchFilteredMatched])

  // QA Bug 2 (HIGH, FE half) / ADR-0130 — a selected id must not survive a
  // status change (ignored via Ignore/"Keep my uploaded part", or confirmed)
  // or a removal — otherwise the action bar keeps counting it as selected
  // and "Confirm & add to library"/"Delete selected" would send its id
  // straight through, silently reversing the user's own Ignore/Remove.
  // Pruning off of `selectableRowIds` (rather than only in each handler)
  // cleans up selection on every path that changes row eligibility.
  useEffect(() => {
    setSelectedStaged((prev) => {
      if (prev.size === 0) return prev
      const validIds = new Set(selectableRowIds)
      const next = new Set([...prev].filter((id) => validIds.has(id)))
      return next.size === prev.size ? prev : next
    })
  }, [selectableRowIds])

  /**
   * ADR-0130 — toggles one row, or (shift-click, per the design's global
   * `data-sel-key="stagedSel"` wiring) extends the selection across the
   * contiguous range since the last row a checkbox click landed on.
   *
   * ADR-0155 D6(b) — the range walk indexes into `visibleSelectableRowIds`
   * (query-aware), NEVER `selectableRowIds` (unfiltered): walking the
   * unfiltered list would extend the range through rows a query is
   * currently hiding, selecting parts the user cannot see and cannot have
   * intended to tick — the same defect class D6(b) named for the eligibility
   * set, on the range-math path instead.
   *
   * Edge case (decided here, per the coordinator's follow-up): if
   * `lastSelectedId` — the anchor — is itself not currently visible (a query
   * was typed after that row was clicked, hiding it), `fromIdx` is `-1` and
   * this block falls through to the plain single-row toggle below rather
   * than falling back to the unfiltered list. An anchor the user can no
   * longer see is not an anchor they can still shift-click from — reusing
   * the unfiltered position would silently reintroduce exactly the
   * invisible-range bug this fix exists to close.
   */
  function toggleStagedSelection(partId: string, shiftKey: boolean) {
    setSelectedStaged((prev) => {
      const next = new Set(prev)
      if (shiftKey && lastSelectedId !== null) {
        const fromIdx = visibleSelectableRowIds.indexOf(lastSelectedId)
        const toIdx = visibleSelectableRowIds.indexOf(partId)
        if (fromIdx !== -1 && toIdx !== -1) {
          const [lo, hi] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx]
          for (let i = lo; i <= hi; i += 1) {
            const id = visibleSelectableRowIds[i]
            if (id !== undefined) next.add(id)
          }
          setLastSelectedId(partId)
          return next
        }
      }
      if (next.has(partId)) next.delete(partId)
      else next.add(partId)
      setLastSelectedId(partId)
      return next
    })
  }

  /**
   * PM ruling (2026-09-01) — one select-all PER SECTION, each scoped to its
   * OWN rows, while the underlying `selectedStaged` map stays the single
   * unified set. The design has one table with one header/select-all
   * (`:3271`'s `onToggleIdent`); this build renders identified and matched
   * as two separate section cards (UID-254 / ADR-0124 D7). Splitting the
   * select-all per section is the faithful translation of "one select-all
   * per table" onto "one table per section" — it is a deliberate structural
   * adaptation, not drift from the design, and it closes the real gap a
   * single identified-only select-all left: a matched-only session (the
   * catalog matched everything, zero identified rows — an expected, common
   * outcome, and exactly the population "Confirm & add to library" exists
   * for) previously had NO select-all at all.
   */
  const identifiedIds = useMemo(
    () => displayedIdentified.map((part) => part.partId),
    [displayedIdentified],
  )
  const matchedSelectableIds = useMemo(
    () => displayedMatched.filter((part) => part.status !== 'ignored').map((part) => part.partId),
    [displayedMatched],
  )

  /**
   * ADR-0155 D6(b), SECURITY FOLLOW-UP — the query-aware siblings of
   * `identifiedIds`/`matchedSelectableIds` above, mirroring
   * `visibleSelectableRowIds`'s reasoning exactly: "select all" and the
   * header checkbox's tri-state must reflect only what is CURRENTLY ON
   * SCREEN, never the full unfiltered population. Before this pair existed,
   * both flat-bucket select-alls read `identifiedIds`/`matchedSelectableIds`
   * directly — so ticking "select all" under a narrowing query silently
   * added every HIDDEN row to `selectedStaged` too, and a subsequent
   * "Delete selected" (`identifiedSelectedIds`/`matchedSelectedIds` below,
   * which correctly stay unfiltered) issued a real `removeStagedPart` for
   * every one of them: a 3-row gesture producing a 200-row, no-undo
   * deletion. `identifiedIds`/`matchedSelectableIds` themselves are UNCHANGED
   * and still feed `identifiedSelectedIds`/`matchedSelectedIds`,
   * `clearSectionSelection`, and the true selected-count pill — "Clear" and
   * "Delete selected" still act on the user's FULL current selection,
   * visible or not (matching the unified BOM section's `onClear`, which has
   * always used the unfiltered `selectableRowIds`) — only the "select ALL"
   * gesture itself is scoped to what "all" can actually mean under a query:
   * everything the user can see right now.
   */
  const identifiedVisibleIds = useMemo(
    () => searchFilteredIdentified.map((part) => part.partId),
    [searchFilteredIdentified],
  )
  const matchedVisibleSelectableIds = useMemo(
    () =>
      searchFilteredMatched.filter((part) => part.status !== 'ignored').map((part) => part.partId),
    [searchFilteredMatched],
  )

  // Tri-state now reflects the VISIBLE population — a query that hides every
  // ticked row (and shows only untouched ones) must not still claim "all"
  // are selected, and select-all must not be reachable for rows the user
  // cannot see to reason about.
  const identifiedAllSelected =
    identifiedVisibleIds.length > 0 && identifiedVisibleIds.every((id) => selectedStaged.has(id))
  const identifiedSomeSelected = identifiedVisibleIds.some((id) => selectedStaged.has(id))

  const matchedAllSelected =
    matchedVisibleSelectableIds.length > 0 &&
    matchedVisibleSelectableIds.every((id) => selectedStaged.has(id))
  const matchedSomeSelected = matchedVisibleSelectableIds.some((id) => selectedStaged.has(id))

  /** Gate-5 cleanup — the "which ids, of this section's own list, are
   * currently selected" derivation, hoisted so the per-section delete
   * handlers and `handleDeleteSelectedBom` below reuse the same computed
   * arrays instead of each re-filtering `selectedStaged` independently.
   * Deliberately UNFILTERED (D6(b)): "Delete selected" must delete exactly
   * what the user selected, including any row a query is currently hiding —
   * the fix above scopes what SELECT-ALL can ADD, not what DELETE can act
   * on. */
  const identifiedSelectedIds = useMemo(
    () => identifiedIds.filter((id) => selectedStaged.has(id)),
    [identifiedIds, selectedStaged],
  )
  const matchedSelectedIds = useMemo(
    () => matchedSelectableIds.filter((id) => selectedStaged.has(id)),
    [matchedSelectableIds, selectedStaged],
  )

  /** PM ruling (2026-09-01) — per-section selection COUNTS, feeding each
   * section's own `StagingSelectionBand` (renders only when its own count is
   * non-zero). */
  const identifiedSelectedCount = identifiedSelectedIds.length
  const matchedSelectedCount = matchedSelectedIds.length

  /**
   * ADR-0155 D6(b) blast-radius disclosure — how many of THIS section's
   * selected rows (`identifiedSelectedCount`/`matchedSelectedCount`, the
   * TRUE total) are currently hidden by the active query, i.e. selected but
   * absent from `identifiedVisibleIds`/`matchedVisibleSelectableIds`. Fed to
   * `StagingSelectionBand`'s `hiddenSelectedCount` so a band showing "200
   * part(s) selected" above a 3-row body says so, rather than leaving the
   * count as the ONLY signal of the mismatch.
   */
  const identifiedHiddenSelectedCount =
    identifiedSelectedCount - identifiedVisibleIds.filter((id) => selectedStaged.has(id)).length
  const matchedHiddenSelectedCount =
    matchedSelectedCount - matchedVisibleSelectableIds.filter((id) => selectedStaged.has(id)).length

  /**
   * Gate-5 C1 (Critical) — on a BOM session the unified section rendered NO
   * `StagingSelectionBand` at all: `StagingActionBar` was already ruled OUT
   * as the home for "Delete selected" (see its own doc comment), and the two
   * per-section bands below both sit inside `!hasBomRows` branches, so
   * bulk-delete/clear-selection were UNREACHABLE the instant a session had a
   * captured BOM hierarchy. ADR-0142 D7(2) authorised collapsing the two
   * bands into ONE with unchanged semantics — it never authorised zero.
   *
   * `selectableRowIds` is already the unified list's own render-order id set
   * (D7(1)) and, post-D13/D14.1 dedupe, one id per part — reused here rather
   * than re-deriving a third id list. Clearing/counting is scoped to exactly
   * those ids (never `identifiedIds`/`matchedSelectableIds` directly, which
   * would double-count nothing today but would silently diverge from what's
   * actually visible if the two ever drifted).
   */
  const bomSelectedCount = selectableRowIds.filter((id) => selectedStaged.has(id)).length

  /**
   * WARNING C (Gate-5 re-review) — the BOM band's own blast-radius
   * disclosure, mirroring `identifiedHiddenSelectedCount`/
   * `matchedHiddenSelectedCount` above exactly: how many of `bomSelectedCount`
   * (the TRUE, unfiltered total) are currently hidden by an active search
   * query, i.e. selected but absent from `visibleSelectableRowIds`. This is
   * the ONLY session type D5's tree pruning runs on, so — before this fix —
   * the band could read "40 part(s) selected" above a 2-row pruned tree with
   * nothing telling the user why the numbers disagreed.
   *
   * Non-negativity, checked rather than assumed (the flat sections' own
   * subtraction is safe only because their visible id list is a strict
   * subset of their unfiltered one — that argument does not carry over for
   * free): `visibleSelectableRowIds` and `selectableRowIds` are built by the
   * IDENTICAL two-branch construction (`collectSelectablePartIds` over the
   * tree, plus the same non-ignored-matched-leaf filter over `ungrouped`),
   * differing only in which `UnifiedBomRows`/flat-array pair feeds them —
   * `searchFilteredUnifiedBomRows`/`searchFilteredIdentified`/
   * `searchFilteredMatched` (visible) vs. `unifiedBomRows`/
   * `displayedIdentified`/`displayedMatched` (unfiltered). `filterBomTree`
   * and `filterUngroupedLeaves` (`staging-part-search.ts`) only ever REMOVE
   * nodes/leaves from their input — never add one that was not already
   * there — so the filtered tree's surviving leaves, and therefore
   * `collectSelectablePartIds`'s output over it, are always a SUBSET of the
   * unfiltered tree's. The same holds for `filterByPartName`'s identity/
   * filter-in-place behaviour over the flat arrays. `visibleSelectableRowIds`
   * is therefore a strict subset of `selectableRowIds` (as sets) whenever a
   * query is active, and identical to it when no query is active — so the
   * subtrahend below can never exceed `bomSelectedCount`, exactly as it
   * cannot for the two flat sections.
   */
  const bomHiddenSelectedCount =
    bomSelectedCount - visibleSelectableRowIds.filter((id) => selectedStaged.has(id)).length

  /** Toggles every row in `ids` on/off together, without disturbing
   * selection state for ids outside this section — each section's
   * select-all governs only its own rows. */
  function toggleSelectAllFor(ids: readonly string[], allSelected: boolean) {
    setSelectedStaged((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (allSelected) next.delete(id)
        else next.add(id)
      }
      return next
    })
  }

  // `originalRowCount` lived here to gate the S-B toolbar's render; with the
  // toolbar gone it had no remaining consumer. The "is anything staged at all?"
  // question it also answered is now `deriveStagingCta`'s `kind: 'none'`.

  /**
   * UID-402 — the session-wide, priceability-derived primary CTA + resolution
   * gate (ADR-0124). Derived from the WHOLE triage, deliberately NOT from
   * `selectedMatched`: "can we price this upload?" is a fact about the upload,
   * not about what the user has ticked.
   */
  const cta = triage ? deriveStagingCta(triage) : null

  /**
   * ADR-0155 D4(1)/D1 — the page-level "showing X of Y" pair, and the
   * toolbar's own visibility gate (design source: `stgHasAnyRow`,
   * `Fictiv Platform.dc.html:3037-3053` — the search bar is gated on "this
   * session has any staged row at all", not on `hasBomRows`, so it stays
   * reachable on a CAD-only session — the dominant, permanent case per this
   * file's own doc comments). `totalRowCount` is built ENTIRELY from
   * `displayed*`/`bomPartRowCount` (unfiltered); `visibleRowCount` from the
   * `searchFiltered*` siblings — never crossed.
   */
  const hasAnyStagedRow =
    displayedFiles.length > 0 ||
    hasBomRows ||
    displayedIdentified.length > 0 ||
    displayedMatched.length > 0
  const totalRowCount =
    displayedFiles.length +
    (hasBomRows ? bomPartRowCount : displayedIdentified.length + displayedMatched.length)
  const visibleRowCount =
    searchFilteredFiles.length +
    (hasBomRows
      ? bomVisiblePartRowCount
      : searchFilteredIdentified.length + searchFilteredMatched.length)

  /**
   * CRITICAL 3 (Gate-5 review) — which sections have rows that exist
   * (unfiltered count > 0) but currently show none (filtered count === 0),
   * in on-screen order. Feeds the ONE consolidated disclosure message below
   * — this is the same fact each section's own visual "no matches" note
   * states individually; computed once here so the single announced region
   * can name every affected section in one sentence instead of one region
   * per section firing its own announcement per keystroke.
   */
  const zeroMatchSectionLabels: readonly string[] = hasActiveSearchQuery
    ? [
        displayedFiles.length > 0 && searchFilteredFiles.length === 0
          ? STAGING_SECTION_LABELS.needsResolution
          : null,
        hasBomRows && bomPartRowCount > 0 && bomVisiblePartRowCount === 0
          ? STAGING_SECTION_LABELS.bomParts
          : null,
        !hasBomRows && displayedIdentified.length > 0 && searchFilteredIdentified.length === 0
          ? STAGING_SECTION_LABELS.identified
          : null,
        !hasBomRows && displayedMatched.length > 0 && searchFilteredMatched.length === 0
          ? STAGING_SECTION_LABELS.matched
          : null,
      ].filter((label): label is string => label !== null)
    : []

  /**
   * CRITICAL 3 (Gate-5 review) — the ONE string every announced/visible
   * disclosure region on this page renders. Empty when idle, so the
   * unconditionally-mounted `<output aria-live="polite">` in
   * `StagingToolbar` announces nothing on first render (ADR-0124 W8's
   * precedent: "a live region must exist BEFORE its content changes"). One
   * region, one message, per keystroke — never multiple simultaneous
   * `aria-live` regions (the "announcement storm"; the awareness was
   * originally documented in `UploadFileList.tsx`, which UID-583 deleted —
   * see `git show main:…/components/UploadFileList.tsx` for that note, and
   * this file's own upload-phase regions below, which now have to honour the
   * same rule).
   */
  const searchDisclosureMessage = !hasActiveSearchQuery
    ? ''
    : zeroMatchSectionLabels.length > 0
      ? `Showing ${visibleRowCount} of ${totalRowCount}. No matches in: ${zeroMatchSectionLabels.join(', ')}.`
      : `Showing ${visibleRowCount} of ${totalRowCount}.`

  /**
   * WARNING E (Gate-5 re-review) — debounces ONLY this announced/visible
   * string, never `searchQuery` or anything `staging-part-search.ts` derives
   * from it for rendering (every `searchFiltered*`/`searchFilteredUnifiedBomRows`
   * above stays instant, so sighted users see the row set narrow on every
   * keystroke with no lag). Without this, a 7-character query queued up to 7
   * distinct content changes on the one consolidated `aria-live="polite"`
   * region — consolidating to a single region (D4's CRITICAL 3 fix) helped
   * because most screen readers coalesce/interrupt successive updates to the
   * SAME region, but did not remove the queue.
   *
   * `immediateWhen: (message) => message === ''` is why clearing the search
   * (or the very first idle render) never waits out the debounce window: an
   * explicit Clear click is a discrete action, not keystroke noise, and
   * D4's own "the live region must reflect the CURRENT state" posture does
   * not tolerate a stale "Showing X of Y" lingering after the rows have
   * already reset to the full set.
   */
  const debouncedSearchDisclosureMessage = useDebouncedValue(
    searchDisclosureMessage,
    SEARCH_DISCLOSURE_DEBOUNCE_MS,
    { immediateWhen: (message) => message === '' },
  )

  // UID-583 (ADR-0173 D5) — not one file's bytes reached S3, so the session
  // is empty and there is nothing for a triage to show. Rendered BEFORE the
  // loading branch because `phase: 'failed'` also means the hold has been
  // released with no data coming: without this arm the analysing panel would
  // spin forever on a batch that has already given up. This is the arm that
  // replaces the dropzone-side `batchError` the user can no longer see,
  // because the flow navigated them here before the transfer failed.
  if (batch?.phase === 'failed') {
    return (
      <EmptyState
        title="Your upload didn’t finish"
        detail={batch.batchError ?? TRANSFER_FAILED_MESSAGE}
        action={<Button onClick={() => navigate('/uploads')}>Back to uploads</Button>}
      />
    )
  }

  if (state.status === 'loading') {
    // The shared `ProgressPanel` (extracted by UID-550, which this story's own
    // panel-removal kept — `StagingPage` is now its only consumer).
    //
    // UID-583 (ADR-0173 D6) — `pct` has TWO sources and the panel is
    // deliberately agnostic about which it is given:
    //   - while the bytes are moving, `batch.pct` — a REAL, byte-weighted
    //     measurement of the browser->S3 transfer (`byteWeightedProgress` over
    //     the flow's own per-file XHR progress) — scaled into the first
    //     `UPLOAD_PHASE_CEILING`% of the bar so the two phases read as one
    //     monotonic progression (Gate-5 W1);
    //   - once they have landed, the openly-simulated `analyzingPct` ramp,
    //     because the analysis phase genuinely has no progress signal:
    //     `StagingTriage.analysisProgress` is server-authoritative but the BFF
    //     hardcodes it to the literal 100
    //     (apps/api/src/modules/parts-library/staging-service.ts:217-225,
    //     pinned by its own test), this branch is only reachable BEFORE the
    //     first `getStagingTriage` response lands (so `triage` is always
    //     `null` here), and there is no polling seam to sample (ADR-0134 R2).
    //
    // The subtitle follows the same rule — a real "x of y files uploaded"
    // count while that is what is happening, then the design's own analysis
    // copy (`Fictiv Platform.dc.html:8214-8219`). Neither line ever claims to
    // be something it is not.
    return (
      <>
        <ProgressPanel
          aria-busy="true"
          aria-label="Loading staging session"
          title="Analyzing your files"
          subtitle={
            transferring && batch !== null
              ? `${batch.settledCount} of ${batch.total} file${
                  batch.total === 1 ? '' : 's'
                } uploaded`
              : 'Cross-referencing drawings, checking tolerances, matching catalog…'
          }
          pct={
            transferring && batch !== null ? (batch.pct * UPLOAD_PHASE_CEILING) / 100 : analyzingPct
          }
          progressLabel={ANALYZING_LABEL}
        />
        {/* Gate-5 C3 + W2 — the upload phase's ONE announcement. Mounted from
            the first render and empty until there is something to say, so the
            0->1 content change is what gets announced (ADR-0124 W8); the
            `ProgressPanel` above deliberately carries NO `aria-live`, because
            its `%` and subtitle change on every XHR progress event and this
            page's own CRITICAL 3 / ADR-0157 rule is one region, one message.
            `sr-only`: the panel already says all of this visually. */}
        <output aria-live="polite" className="sr-only">
          {uploadAnnouncement}
        </output>
      </>
    )
  }

  if (state.status === 'not_found') {
    return (
      <EmptyState
        title="Staging session not found"
        detail="This upload session may have been removed, or you may not have access to it."
        action={<Button onClick={() => navigate('/uploads')}>Back to uploads</Button>}
      />
    )
  }

  if (state.status === 'error') {
    return (
      <EmptyState
        title="We couldn’t load this session"
        detail={state.message}
        action={<Button onClick={refetch}>Try again</Button>}
      />
    )
  }

  if (!triage) return null

  /** QA Bug 1 (HIGH) — the one place every row-level `applyMutation` call now
   * funnels through: awaited and caught, with the failure surfaced via
   * `mutationError` (rendered as a page-level `role="alert"`) instead of a
   * dangling, user-invisible rejection.
   *
   * ADR-0156 D4 (Gate-5 fix) — returns `true`/`false` instead of `void` (and
   * still never throws/rejects, so every EXISTING `void runMutation(...)`
   * call site above is unaffected). `StagingBomLeafRow`'s `QuantityInput` is
   * the one caller that reads the boolean, to know whether to revert its
   * own local draft on a rejected edit — the error itself is still reported
   * exactly as before, via `mutationError`, not by this return value. */
  async function runMutation(mutate: () => Promise<StagingTriage>): Promise<boolean> {
    setMutationError(undefined)
    try {
      await applyMutation(mutate)
      return true
    } catch (error) {
      setMutationError(
        error instanceof Error ? error.message : 'That action didn’t go through. Try again.',
      )
      return false
    }
  }

  /**
   * ADR-0157 D2 — the arm dispatch (hard remove vs. soft dismiss) happens
   * SERVER-SIDE, off the node's render state; this client sends nothing to
   * pick it. So neither `handleRemoveCatalogNode` nor `handleDismissNode`
   * may ASSUME which one happened — each reads the REFRESHED triage the
   * mutation returns and checks whether the node is still there. Absent =
   * hard removed (focus must move, since the row is gone). Present with
   * `dismissedAt` set = soft dismissed (the row stays — `StagingBomLeafRow`
   * renders that from the SAME triage update, no separate signal needed —
   * and there is nothing here to move focus off of).
   */
  /**
   * ADR-0157 D1/D2 — a catalog leaf's own Remove: computes the focus
   * fallback BEFORE the mutation runs (off the PRE-removal tree — the
   * removed node's siblings, unaffected by its own removal), then only
   * commits the focus target/announcement once the mutation actually
   * SUCCEEDS (a refused delete leaves the row rendered, per D2.3's own
   * merge-blocking test, and must not move focus off a row that is still
   * there) AND the node is actually gone from the refreshed triage — see
   * `isNodeDismissed` above.
   */
  async function handleRemoveCatalogNode(
    nodeId: string,
    name: string,
    occurrenceCount: number,
  ): Promise<void> {
    const fallback = findRemovalFocusFallback(unifiedBomRows.tree, unifiedBomRows.ungrouped, nodeId)
    setMutationError(undefined)
    let nextTriage: StagingTriage
    try {
      nextTriage = await service.removeBomNode(sessionId, nodeId)
    } catch (error) {
      setMutationError(
        error instanceof Error ? error.message : 'That action didn’t go through. Try again.',
      )
      return
    }
    await applyMutation(() => Promise.resolve(nextTriage))
    if (findDismissedAt(nextTriage.bomHierarchy ?? [], nodeId) !== null) {
      // The server decided this render state is a SOFT dismiss (e.g. a
      // catalog leaf whose SKU turned out to resolve against nothing) — the
      // row stays, so there is nothing to move focus off of. Same wording
      // `handleDismissNode` uses, for the same reason.
      setLiveAnnouncement(`Dismissed ${name}. Your stated quantity is kept.`)
      return
    }
    setPendingFocusTarget(fallback)
    // Gate-5 W4 — CORRECTED FALSE ANNOUNCEMENT. This said "Removed ${name} from
    // all N positions in this BOM." `dropBomNode` matches a single
    // `node.nodeId === nodeId` and removes ONE node, so that was a false
    // user-facing claim about a destructive, irreversible action (ADR-0157
    // Am.1 D4 gives this arm no Undo).
    //
    // THE CODE IS RIGHT AND THE COPY WAS WRONG, which is why the fix is here:
    // ADR-0157 D1/D2 scopes this verb to the BOM LINE ("per-BOM-line delete"),
    // so removing one position is correct. What the user needs told is that the
    // part remains at its other positions — the opposite of what they were
    // being told.
    //
    // NOTE the deliberate asymmetry with the BULK delete, which DOES remove
    // every position (Gate-5 C-3): its subject is the selected PART, not a
    // line, and leaving the row rendered after a "successful" delete was the
    // silent no-op that finding is about. Two verbs, two subjects, and now two
    // truthful announcements.
    setLiveAnnouncement(
      occurrenceCount > 1
        ? `Removed ${name} from this BOM line. It is still on ${occurrenceCount - 1} other ${occurrenceCount - 1 === 1 ? 'line' : 'lines'}.`
        : `Removed ${name}.`,
    )
  }

  /**
   * ADR-0157 D3 — dismisses an unresolved leaf. Never moves focus (the row
   * stays in place and the button swaps to Undo in the same cell — no
   * focus-management gap to close here, unlike Remove above), but still
   * announces the result through the SAME page-level `aria-live` region,
   * with the second clause the human's spec calls out as what makes the
   * verb legible as non-destructive.
   */
  async function handleDismissNode(
    nodeId: string,
    name: string,
    bomLine: number | null,
    siblingIndex: number,
  ): Promise<void> {
    const ok = await runMutation(() => service.removeBomNode(sessionId, nodeId))
    if (!ok) return
    setLiveAnnouncement(
      `Dismissed ${formatBomLineLabel(bomLine, siblingIndex)}, ${name}. Your stated quantity is kept.`,
    )
  }

  function handleUndoDismissNode(nodeId: string): void {
    void runMutation(() => service.restoreBomNode(sessionId, nodeId))
  }

  /**
   * ADR-0130 (UID-425 Bug 2 / PM ruling 2026-09-01) — "Delete selected" goes
   * through the SAME real `removeStagedPart` path the per-row Remove button
   * uses — no new client-only "hidden" state, no fake persistence. Shared by
   * BOTH per-section bands (`deleteStagedIds` takes the exact ids and the
   * setters to report through, so each band's failure/busy state stays
   * scoped to that band, not the other section's).
   *
   * BEHAVIOUR DECISION for a bulk failure ("coherent if one fails"):
   * best-effort and sequential. Each selected id is removed one at a time
   * (sequential, not parallel, so each real DELETE observes the previous
   * one's result rather than racing against it); a failure on one id does
   * NOT abort the rest — every other selected id in THIS section is still
   * attempted. A row that failed stays selected AND stays on screen (the
   * removal never happened server-side, so nothing here pretends it did);
   * rows that succeeded are gone for real, because `applyMutation` commits
   * the server's returned triage after every successful call. On any failure
   * the count of failures is reported, naming that the rest can be retried —
   * never a false "all removed" success.
   */
  async function deleteStagedIds(
    ids: readonly string[],
    setDeleting: (value: boolean) => void,
    /** Gate-5 W3 — `null` means "return the count, do not write a message": the
     * combined BOM band aggregates across two calls and writes one. */
    setError: ((value: string | undefined) => void) | null,
    // Gate-5 warning fix — the DENOMINATOR the failure message names. Defaults
    // to `ids.length` (this call's own band — correct for a per-section
    // Delete click, where "the band the user actually clicked" IS this
    // array). `handleDeleteSelectedBom` below passes the COMBINED
    // identified+matched selection count instead, because on the unified BOM
    // band, the band the user actually clicked spans both underlying arrays —
    // without this, selecting 4 BOM rows (2 identified + 2 matched) with one
    // identified failure reported "1 of the 2 selected parts", silently
    // dropping the 2 matched rows from the count the user actually acted on.
    totalSelectedCount: number = ids.length,
  ) {
    // Gate-5 warning fix — clear this band's stale error on EVERY call,
    // including an empty-`ids` one, BEFORE the early return below. The old
    // order returned early on `ids.length === 0` first, so a matched-only
    // delete (identified ids empty) never cleared a STALE identified error
    // from an earlier failed identified delete — and `StagingSelectionBand`'s
    // combined BOM-band message concatenates both bands' errors into one
    // `role="alert"`, so that stale identified message would ride along next
    // to the fresh matched one.
    setError?.(undefined)
    if (ids.length === 0) return 0
    setDeleting(true)
    let failedCount = 0
    try {
      for (const id of ids) {
        try {
          // Gate-5 C4 — DISPATCH ON THE ARM. `removeStagedPart` is
          // `partId`-scoped and searches the PERSISTED triage, where a catalog
          // leaf does not exist (no `intake.staged_file` row, migration 1013's
          // arm mutex), so it 404s for every one of them. Opening the row
          // checkbox on catalog leaves made this path reachable, so the bulk
          // delete routes them to the SAME node-scoped verb the row's own trash
          // icon uses.
          if (isCatalogLeafPartId(id)) {
            // Gate-5 C-3/C-D — removes EVERY BOM position the row stands for,
            // and NOW ACTUALLY DOES. `dropBomNode` removes one node, so a row
            // disclosing "also on 1 other line" needs one call per position.
            //
            // The previous two attempts are worth knowing about. Round 3 wrote
            // this loop against `unifiedBomRows.tree` — the DEDUPED tree, which
            // holds exactly one node per `partId` by construction — so the loop
            // was correct in shape and degenerate in practice: it did exactly
            // what the single-call version did, while reading as a fan-out. The
            // fix was not available at this call site at all; it needed the view
            // model to stop discarding the pre-dedupe ids, which it now exposes
            // as `nodeIdsByPartId`.
            //
            // UNFILTERED `unifiedBomRows`, not `searchFilteredUnifiedBomRows` —
            // per ADR-0155 D3/D6(b) and because a delete's blast radius must not
            // depend on the search box.
            const nodeIds = findNodeIdsForPartId(unifiedBomRows, id)
            // Gate-5 C-4 — EMPTY IS A REFUSAL, not a reason to fall through.
            // The previous fix fell back to `removeStagedPart` when no node was
            // found, dispatching a verb it had just determined BY NAMESPACE
            // could not succeed — reproducing, character for character, the
            // "We couldn't remove 1 of the 1 selected parts" dead end that
            // opening the checkbox was supposed to stop. Counting it as a
            // failure is the honest outcome.
            if (nodeIds.length === 0) {
              throw new Error(
                `no BOM node in the rendered tree carries catalog part ${id}; refusing to send it to the part-scoped delete, which cannot address a catalog leaf`,
              )
            }
            // Sequential, and the LOOP IS THE UNIT: a mid-way failure throws to
            // the outer catch, which counts this id as failed and leaves it
            // selected, so a retry re-attempts the whole row. Removing the
            // earlier positions and reporting success would be the silent
            // partial this finding is about.
            for (const nodeId of nodeIds) {
              await applyMutation(() => service.removeBomNode(sessionId, nodeId))
            }
          } else {
            await applyMutation(() => service.removeStagedPart(sessionId, id))
          }
          setSelectedStaged((prev) => {
            const next = new Set(prev)
            next.delete(id)
            return next
          })
        } catch {
          failedCount += 1
        }
      }
    } finally {
      setDeleting(false)
    }
    // Gate-5 W3 — the caller may take the count instead of a message. On the
    // unified BOM band this function is invoked TWICE (identified, then
    // matched), each call knowing only its OWN failures while both were handed
    // the COMBINED denominator — so one failure in each band produced "1 of the
    // 3" twice, concatenated, and "2 of the 3" was never said. An
    // off-by-PARTITION, not an off-by-one. The BOM caller now sums the returns
    // and writes ONE message.
    if (failedCount > 0 && setError !== null) {
      setError(failureMessage(failedCount, totalSelectedCount))
    }
    return failedCount
  }

  /** The ONE place the bulk-delete failure copy is built, so the per-section and
   * combined-band callers cannot word it differently (Gate-5 W3). */
  function failureMessage(failedCount: number, totalSelectedCount: number): string {
    return `We couldn’t remove ${failedCount} of the ${totalSelectedCount} selected part${
      totalSelectedCount === 1 ? '' : 's'
    }. The rest were removed; try again for the remaining ${failedCount === 1 ? 'one' : 'ones'}.`
  }

  async function handleDeleteSelectedIdentified() {
    await deleteStagedIds(
      identifiedSelectedIds,
      setIdentifiedBulkDeleting,
      setIdentifiedBulkDeleteError,
    )
  }

  async function handleDeleteSelectedMatched() {
    await deleteStagedIds(matchedSelectedIds, setMatchedBulkDeleting, setMatchedBulkDeleteError)
  }

  /**
   * Gate-5 C1 fix — the unified BOM section's ONE `StagingSelectionBand`
   * still targets exactly the same underlying rows a non-BOM session's two
   * bands would: a selected row in the tree is still, underneath, either an
   * identified or a matched part (`partKind`), so this reuses the same
   * hoisted `identifiedSelectedIds`/`matchedSelectedIds` derived ids that
   * `handleDeleteSelectedIdentified`/`handleDeleteSelectedMatched` use —
   * never a third selection source — regardless of whether the buckets or
   * the unified tree is what's on screen.
   *
   * It does NOT delegate to those two handlers, though (that was the Gate-5
   * warning fix): their default `totalSelectedCount` denominator is each's
   * OWN array length, which is wrong for this combined band (see
   * `deleteStagedIds`'s `totalSelectedCount` doc comment) — so this computes
   * `totalSelectedCount` itself, once, across both arrays, and calls
   * `deleteStagedIds` directly for each. The identified-then-matched
   * sequencing is STRUCTURAL (`try/finally`), not merely incidental on
   * `deleteStagedIds` never rethrowing (it swallows every per-id failure
   * into `failedCount`): a future change to that swallowing behaviour would
   * otherwise silently skip the matched-band delete entirely.
   */
  async function handleDeleteSelectedBom() {
    const totalSelectedCount = identifiedSelectedIds.length + matchedSelectedIds.length
    // Gate-5 W3 — ONE message across both bands. Each call is told NOT to write
    // its own (`null`), their failure counts are summed, and the single truthful
    // message lands on one band. Previously each band wrote "N of the
    // <combined>" from its own N, the two were concatenated, and the real total
    // was never stated.
    let failed = 0
    setIdentifiedBulkDeleteError(undefined)
    setMatchedBulkDeleteError(undefined)
    try {
      failed += await deleteStagedIds(
        identifiedSelectedIds,
        setIdentifiedBulkDeleting,
        null,
        totalSelectedCount,
      )
    } finally {
      failed += await deleteStagedIds(
        matchedSelectedIds,
        setMatchedBulkDeleting,
        null,
        totalSelectedCount,
      )
    }
    if (failed > 0) {
      // WHICH band carries the single message matters: each
      // `StagingSelectionBand` renders only while its OWN selection is
      // non-empty, so writing to a band whose selection is empty produces an
      // invisible error. The unified BOM band reads both slots (falling back to
      // whichever is set), so this picks a band that is still rendered —
      // preferring identified, which renders first.
      const message = failureMessage(failed, totalSelectedCount)
      if (identifiedSelectedIds.length > 0) {
        setIdentifiedBulkDeleteError(message)
      } else {
        setMatchedBulkDeleteError(message)
      }
    }
  }

  /** PM ruling — the design's `:3139` `Clear` affordance, scoped per section:
   * deselecting the identified band's rows must not touch the matched
   * section's selection, and vice versa. */
  function clearSectionSelection(ids: readonly string[]) {
    setSelectedStaged((prev) => {
      const next = new Set(prev)
      for (const id of ids) next.delete(id)
      return next
    })
  }

  /** ADR-0130 (Gap 2) — the ONE place both remaining confirm call sites
   * (`handleReviewInLibraryConfirmed` / `handleCreateRfqConfirmed`) decide
   * whether a `projectLink` outcome deserves a warning (see
   * `staging-project-link.ts`'s doc comment for why sharing this matters —
   * hand-rolling it twice is the exact ADR-0124 W1 mistake). Both callers
   * navigate away immediately after, so the warning is a TOAST fired BEFORE
   * `navigate`, because `ToastProvider` mounts ABOVE the router (`App.tsx`),
   * so it survives the route change — the navigation payload
   * (`location.state`) was rejected because `PartsLibraryPage`'s existing
   * consumer of `highlightPartIds` has no rendering seam for a second,
   * unrelated warning, and inventing one would be an out-of-vertical change.
   * Suppressing the navigation is not an option: the confirm write already
   * succeeded.
   *
   * `projectLink` is typed as required on the wire contract, but this
   * handler tolerates it being absent (a pre-Gap-2 fixture/mock) rather than
   * throwing mid-hand-off and turning a successful write into an apparent
   * failure — the SAME defensive posture this file already takes for every
   * other additive/optional field. */
  function handleProjectLinkOutcome(projectLink: ConfirmProjectLinkOutcome | undefined) {
    if (!projectLink) return
    const message = projectLinkWarningMessage(projectLink, triage?.workspaceName)
    if (!message) return
    showToast(message, 'info')
  }

  /**
   * UID-402 BUG FIX — "Review in library" didn't surface the resolved parts.
   *
   * It used to be a bare `navigate('/parts')` (ADR-0079 §2.2 specified exactly
   * that). Two things were wrong with it against the reviewer's expectation of
   * the secondary option — *"review in library (**add** + **see** parts in the
   * library)"*:
   *
   * 1. It never ADDED anything. A matched staging part is not a Library part
   *    until `confirmParts` writes it (the same fact the add-to-BOM 404 fix
   *    below turns on), so arriving at `/parts` showed a library that did not
   *    contain this session's parts at all.
   * 2. It never SURFACED anything. Even for a previously-confirmed session, the
   *    library is a flat `updatedAt DESC` list — this upload's parts sit
   *    somewhere near the top, unlabelled and interleaved with anything else
   *    recently touched.
   *
   * So: confirm every confirmable matched part (session-wide, matching the
   * primary CTA's posture — NOT `selectedMatched`, since this button is not a
   * selection action), then hand the resulting Library ids to `/parts` through
   * router `location.state`.
   *
   * WHY `location.state` and not a `?ids=` param or a `sessionStorage` store:
   * ADR-0056 D1 already chose router state over a URL param for the analogous
   * RFQ-list hand-off, and it is the far cheaper correct answer here — the
   * payload is a one-shot navigation argument, so it dies with the navigation
   * and inherits none of ADR-0103's obligations (schema versioning, `accountId`
   * stamping, wipe-on-logout) that a persisted tenant-scoped snapshot would
   * carry. Ids in the URL would additionally make a foreign-tenant id
   * pasteable. See ADR-0124.
   *
   * `confirmParts` is all-or-nothing (ADR-0060): ONE unconfirmable part 409s
   * every part in the batch, so the selection is filtered through
   * `isMatchedConfirmable` — which mirrors all four of the server's
   * `applyConfirmParts` guards — BEFORE the call, rather than sinking the whole
   * hand-off. Gate-5 W1: this used to be a hand-rolled `status !== 'ignored' &&
   * sourcingOptions.length > 0` filter here, which MISSED the
   * `matchType === 'manual'` guard (ADR-0066), so a session containing a
   * user-picked demo match failed the entire hand-off with a 409 reported as a
   * generic error. Re-confirming an already-`confirmed` part is idempotent,
   * which is what lets a second visit re-surface the same parts. A confirm
   * failure is surfaced and the navigation is abandoned — silently landing on an
   * unchanged library is precisely the bug being fixed.
   */
  async function handleReviewInLibrary() {
    // NOT dead code, despite the `if (!triage) return null` render guard above:
    // TypeScript does not narrow `triage` inside these handler closures, so
    // removing this is a TS18047 compile error (verified).
    if (!triage) return
    const confirmable = triage.matched.filter(isMatchedConfirmable)
    if (confirmable.length === 0) {
      // Nothing this page can add to the Library (an all-identified session, or
      // everything ignored/manual). Navigate straight through: there is no
      // write to perform, so this is just a link.
      navigate('/parts')
      return
    }
    await handleReviewInLibraryConfirmed(confirmable.map((part) => part.partId))
  }

  /** Runs the "Review in library" hand-off directly — no confirmation prompt. */
  async function handleReviewInLibraryConfirmed(partIds: readonly string[]) {
    setHandoffBusy(true)
    setHandoffError(undefined)
    try {
      const result = await service.confirmParts(sessionId, partIds)
      await applyMutation(() => Promise.resolve(result.triage))
      setSelectedStaged((prev) => {
        const next = new Set(prev)
        for (const id of partIds) next.delete(id)
        return next
      })
      // Fires the toast BEFORE navigating — see `handleProjectLinkOutcome`'s
      // doc comment for why a toast, not the navigation payload.
      handleProjectLinkOutcome(result.projectLink)
      navigate('/parts', {
        state: { highlightPartIds: [...result.confirmedLibraryPartIds] },
      })
    } catch (error) {
      // The failure is reported beside the hand-off button (W5) — there is no
      // dialog to surface it on.
      setHandoffError(
        error instanceof Error
          ? error.message
          : 'We couldn’t add these parts to your library. Try again.',
      )
    } finally {
      setHandoffBusy(false)
    }
  }

  /**
   * ADR-0130 D3 (UID-425 Bug 3) — "Create RFQ" didn't carry the upload's
   * parts over. ADR-0125 D1 refused the tempting partial fix (wiring only the
   * catalog `matched` population) as WORSE than the empty screen: that hands
   * over exactly the parts that did NOT need an RFQ while silently dropping
   * the custom ones that did — a session with 3 custom parts and 2 priced
   * matches would land a compose screen containing only the 2, submittable.
   *
   * The design's own handler (`Fictiv Platform.dc.html:6204-6209`) settles
   * how: `confirmSession(id)` FIRST, unconditionally, then keys the RFQ lines
   * to the library rows that write just created — "All parts here move
   * together — Create RFQ bundles every part in this batch into one RFQ,
   * priced or not." So: MATERIALISE into the Library first (never fabricate a
   * client-side Sourceable — `confirmParts` mints the real `SourceableRef` via
   * `makeSourceableRef` and its own invariants), then hand the minted
   * sourceables straight to the RFQ bundle.
   *
   * Session-wide, like the primary CTA itself (ADR-0124 D2) and D8's "Review
   * in library" — NOT `selectedStaged`. `confirmParts` is all-or-nothing
   * (ADR-0060), so BOTH populations are pre-filtered with the single-owner
   * predicates from `@misumi/shared` before batching:
   * `isIdentifiedConfirmable` (identified) and `isMatchedConfirmable`
   * (matched, which already excludes `ignored`) — hand-rolling this a second
   * time is exactly the mistake ADR-0124 Gate-5 W1 shipped as a real bug.
   */
  async function handleCreateRfq() {
    if (!triage) return
    const confirmableIds = [
      ...triage.identified.filter(isIdentifiedConfirmable).map((part) => part.partId),
      ...triage.matched.filter(isMatchedConfirmable).map((part) => part.partId),
    ]
    if (confirmableIds.length === 0) {
      // The OPPOSITE of D8's "nothing to add" case: D8 still navigates
      // because the user asked to go to the library. Arriving at `/rfq/new`
      // with an empty bundle IS the reported bug, so this must NOT navigate —
      // explain why instead.
      setRfqHandoffError(
        'Nothing here can be added to an RFQ yet — every match is a demo/manual pick or set aside.',
      )
      return
    }
    setRfqHandoffError(undefined)
    await handleCreateRfqConfirmed(confirmableIds)
  }

  /** Runs the RFQ hand-off (ADR-0130 D3) directly — no confirmation prompt. */
  async function handleCreateRfqConfirmed(partIds: readonly string[]) {
    setRfqHandoffBusy(true)
    setRfqHandoffError(undefined)
    try {
      const result = await service.confirmParts(sessionId, partIds)
      await applyMutation(() => Promise.resolve(result.triage))
      setSelectedStaged((prev) => {
        const next = new Set(prev)
        for (const id of partIds) next.delete(id)
        return next
      })
      // The client binds `confirmedParts` almost directly — no re-deriving
      // `kind`/`uom`/`accountId` (`ConfirmedStagedPart` is field-for-field
      // `RfqBundleItem`, ADR-0130 D3). UID-553 is the ONE mapping: the wire
      // carries the seeded staging `configuration`, which the RFQ bundle wants
      // as a `specSeed` (a `SpecSelection`) to pre-populate the Configure step.
      // `quantity` already shares its name/shape with `RfqBundleItem.quantity`,
      // so it rides through `...rest` untouched.
      //
      // UID-552 (ADR-0170) — `beginDraft`, not `addItems`: this hand-off is a
      // "Create RFQ" entry point and must start a genuinely NEW draft. The
      // bundle write must still precede `navigate` (unchanged) — see
      // `useRfqAnalysis.ts`'s doc comment for the mount-time snapshot
      // constraint this ordering satisfies. `beginDraft` seeds `specSeed` into
      // the new slot's `draft.specSelections` the same first-wins way
      // `addItems` does (both go through `seedDraftMetaFromItems`), so UID-553's
      // seed survives the switch of entry point.
      rfqBundle.beginDraft(
        result.confirmedParts.map(({ configuration, ...rest }) => ({
          ...rest,
          ...(configuration !== null ? { specSeed: toSpecSeed(configuration) } : {}),
        })),
      )
      // Fires the toast BEFORE navigating — see `handleProjectLinkOutcome`'s
      // doc comment for why a toast, not the navigation payload.
      handleProjectLinkOutcome(result.projectLink)
      // MUST run before `navigate`: `useRfqAnalysis.ts` snapshots the
      // bundle's lines ONCE, at `RfqReviewPage` mount (ADR-0125 D1's
      // non-obvious constraint). Populating the bundle after arrival would
      // fix the table but leave the "Preparing your RFQ" entry animation
      // dead — this ordering is what activates it.
      navigate('/rfq/new')
    } catch (error) {
      // Gate-5 W5 pattern: the failure is reported inline, beside the
      // primary CTA (`StagingSummaryCard` renders `rfqHandoffError`), rather
      // than inside a confirmation dialog — there is no dialog here.
      setRfqHandoffError(
        error instanceof Error ? error.message : 'We couldn’t create this RFQ. Try again.',
      )
    } finally {
      setRfqHandoffBusy(false)
    }
  }

  return (
    <section aria-labelledby="staging-heading" className="staging-page">
      <button type="button" className="staging-breadcrumb" onClick={() => navigate('/uploads')}>
        <LeftOutlined aria-hidden="true" /> All uploads
      </button>

      <div className="staging-header">
        <div className="staging-header-title-row">
          <h1 id="staging-heading" className="display-heading">
            Review upload
          </h1>
          {/* ADR-0130 (Bug 4 item 1) — the design binds `{{ sessName }}`
              (`Fictiv Platform.dc.html:2978`), not the raw session id.
              `sessionName` is optional (additive contract field); fall back
              to the id rather than printing "undefined" when absent. */}
          <span className="staging-session-chip">{triage.sessionName ?? sessionId}</span>
        </div>
      </div>
      <p className="staging-subtitle">
        We sorted your upload into what’s identified, what matched the catalog, and what still needs
        you. Resolve the last group and add it all to your library.
      </p>

      {/* UID-583 (ADR-0173 D5) — per-file transport failures.
          WHY THIS EXISTS AT ALL: the deleted upload manifest's `Failed —
          {reason}` chip was the ONLY surface in the app that named a file
          whose bytes never reached S3. The triage cannot do it — its
          `filesToResolve` rows are built from `staged_file` records without
          head-objecting S3, and `not-uploaded`
          (`complete-upload-session.ts`) is a BOM-file-PARSE outcome, not a
          per-file transport one. Removing the manifest without this would
          have traded a visible panel for a silent data loss: the file would
          simply be missing from the session with no explanation anywhere.
          `aria-live="off"` on the `<output>` (Gate-5 W2): `<output>` carries
          an IMPLICIT polite live-region role, and a second live region on
          this page violates its own CRITICAL 3 / ADR-0157 rule of exactly one
          — the same trap `StagingSelectionBand` documents falling into. The
          content IS announced, once, through that single page-level region
          (see `failureNoticeCount`'s effect above). Kept as `<output>` rather
          than a `<div>` for its `role="status"` semantics, which is what
          `role="alert"` would over-state: the upload as a whole SUCCEEDED
          (at least one file landed, or the `'failed'` phase above would have
          taken over), so this is important but not an error interrupting the
          user's task. */}
      {/* Gate-5 C2 — an upload that was interrupted mid-flight (a reload)
          never ran `completeUploadSession`, so this session's BOM hierarchy
          was never captured and there is no polling seam to pick it up
          (ADR-0134 R2). The table below is therefore real but FLAT, and
          before this said so the user had no way to know why. Only shown when
          there is no live batch record — a running upload speaks for itself
          through the analysing panel. Reuses the same notice block rather than
          adding a second style. */}
      {batch === null && uploadWasInterrupted && !uploadFailuresDismissed && (
        <output aria-live="off" className="staging-upload-failures">
          <div className="staging-upload-failures-body">
            <p className="staging-upload-failures-title">This upload was interrupted</p>
            <p className="staging-upload-failures-remedy">
              The page reloaded before the upload finished, so we couldn’t read the assembly
              structure. Everything that made it is listed below — re-upload the BOM spreadsheet if
              you need the hierarchy.
            </p>
          </div>
          <Button
            variant="link"
            size="sm"
            onClick={() => setUploadFailuresDismissed(true)}
            aria-label="Dismiss the interrupted-upload notice"
          >
            Dismiss
          </Button>
        </output>
      )}

      {batch !== null && failureNoticeCount > 0 && !uploadFailuresDismissed && (
        <output
          aria-live="off"
          aria-labelledby="staging-upload-failures-title"
          className="staging-upload-failures"
        >
          <div className="staging-upload-failures-body">
            <p id="staging-upload-failures-title" className="staging-upload-failures-title">
              {failureNoticeCount === 1
                ? '1 file from this upload was not added'
                : `${failureNoticeCount} files from this upload were not added`}
            </p>
            {batch.failedFiles.length > 0 && (
              <>
                <ul className="staging-upload-failures-list">
                  {batch.failedFiles.map((failed) => (
                    // Keyed by row `id`, NOT `fileName` (Gate-5 W3): filenames
                    // are not unique within a batch — two dragged folders can
                    // each contain `bom.csv`, which is the same reason
                    // `useUploads.ts` correlates tickets by index and never by
                    // name (ADR-0135 D4).
                    <li key={failed.id}>
                      <span className="staging-upload-failures-name">{failed.fileName}</span>
                      {` — ${failed.error}`}
                    </li>
                  ))}
                </ul>
                <p className="staging-upload-failures-remedy">
                  {batch.failedFiles.length === 1
                    ? 'Re-upload it from Uploads to add it to your library.'
                    : 'Re-upload them from Uploads to add them to your library.'}
                </p>
              </>
            )}
            {/* Gate-5 C1 — the workbook gate's rejects. A SEPARATE group with
                its OWN remedy, because retrying an `.xlsx` byte-for-byte fails
                identically: the remedy is to save it as CSV. Before this,
                these rows were in no batch state at all and a mixed batch
                (nine `.step` + one `.xlsx`) uploaded the nine and explained
                the one NOWHERE — the `.xlsx` simply was not in the session. */}
            {batch.refusedFiles.length > 0 && (
              <ul className="staging-upload-failures-list">
                {batch.refusedFiles.map((refused) => (
                  <li key={refused.id}>
                    <span className="staging-upload-failures-name">{refused.fileName}</span>
                    {` — ${refused.error}`}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {/* `link` + `sm`: reuses the shared `Button` primitive rather than
              adding a bespoke dismiss control with its own CSS. There is no
              `ghost` variant in `ButtonVariant`, and inventing one for this
              single use would change a shared primitive's surface. */}
          <Button
            variant="link"
            size="sm"
            onClick={() => setUploadFailuresDismissed(true)}
            aria-label="Dismiss the upload-failure notice"
          >
            Dismiss
          </Button>
        </output>
      )}

      {mutationError && (
        <p role="alert" className="staging-row-inline-error staging-page-error">
          {mutationError}
        </p>
      )}

      {/* ADR-0157 — the ONE page-level `aria-live="polite"` region every
          Remove/Dismiss result announcement funnels through (never a
          per-row live region — the human's spec). Visually hidden; its only
          job is to be announced. */}
      <p aria-live="polite" className="sr-only">
        {liveAnnouncement}
      </p>

      {/* Measured off the running design prototype: the search bar is not a
          free-standing page element — it is welded in as the SECOND ROW of
          the summary card, sharing that card's border, with a top inset
          divider between the two rows (`Fictiv Platform.dc.html:3044`'s own
          comment says it: "SEARCH + FILTER bar (unified into the summary
          card)"). This wrapper reproduces that grouping WITHOUT moving
          `StagingToolbar` into `StagingSummaryCard`: search stays a
          page-level component filtering every section uniformly, which is
          ADR-0155 D1/D6(a)'s requirement and the reason it is not nested in
          the BOM-only section (see `StagingToolbar`'s own doc comment). Only
          the card chrome moves — onto the wrapper, which now owns the border
          both rows share. */}
      <div className="staging-summary-and-search">
        <StagingSummaryCard
          summary={triage.summary}
          // 2026-09-08 defect fix — see StagingSummaryCard's `hasBomSource`
          // doc comment: no dedicated spreadsheet-count field exists on the
          // contract yet, so a non-empty captured BOM tree (ADR-0086) is the
          // closest available signal that this session had a BOM/spreadsheet
          // source.
          hasBomSource={(triage.bomHierarchy?.length ?? 0) > 0}
          cta={cta}
          ctaBusy={handoffBusy}
          handoffError={handoffError}
          // ADR-0130 D3 — the RFQ hand-off's OWN error (W5: never shared with
          // the "Review in library" flags above). No dialog to suppress it
          // behind — this is the only surface it renders on.
          rfqHandoffError={rfqHandoffError}
          onReviewInLibrary={handleReviewInLibrary}
          onCreateRfq={handleCreateRfq}
          onCheckout={() => navigate('/cart')}
        />

        {/* ADR-0155 — the S-B toolbar is back, search-only, page-level (not
            nested in the "BOM parts" section's own toolbar slot — see
            `StagingToolbar`'s doc comment for why). Gated on `hasAnyStagedRow`
            — the design's own `stgHasAnyRow` — so it stays reachable on a
            CAD-only session with no BOM section at all. */}
        {hasAnyStagedRow && (
          <StagingToolbar
            query={searchQuery}
            onQueryChange={setSearchQuery}
            disclosureMessage={debouncedSearchDisclosureMessage}
          />
        )}
      </div>

      {/* UID-402 / (2026-09-04) — the sticky selection/action card that used
          to sit here (S-G, `StagingActionBar`) is REMOVED in full: it does
          not exist in the design. That takes "Add to BOM" and "Confirm & add
          to library" with it — neither has a home on this page any more. The
          single primary CTA now lives in `StagingSummaryCard`, where the
          canonical design source puts it (`Fictiv Platform.dc.html:3040`). */}

      {/* ADR-0155 D4(3) restores, in substance, the "Nothing matches your
          filter" empty state UID-402 (ADR-0124 D6) removed as unreachable —
          it is reachable again now that search is back. It is NOT a
          wrapping ternary this time (D4(4) forbids that shape here — a
          section that has real rows must stay ON SCREEN with its true
          unfiltered count pill even when a query matches none of them): each
          section below renders its OWN small "no matches" message INSIDE its
          existing body when `hasActiveSearchQuery` and its own filtered
          population is empty, while the section's outer visibility gate
          keeps reading the UNFILTERED count. The genuinely-empty-SESSION
          state is unaffected: every section still self-hides (D7) when its
          unfiltered population is zero, and the summary card's CTA still
          reads `kind: 'none'`. */}
      {/* UID-254 — three flat buckets keyed directly off the triage
              arrays (reverses ADR-0079's two-state "Needs attention"/"Ready
              to add" merge).

              UID-402 changes TWO things about this block:
              (1) ORDER — "Needs resolution" is now FIRST, not last. It is the
                  only bucket that gates the primary CTA, so burying it under
                  two longer lists made the one blocking task the least visible
                  thing on the page. The canonical design source already ordered
                  it this way (`Fictiv Platform.dc.html:3056` "SECTION: Needs
                  attention (files to resolve)" precedes `:3267` "SECTION:
                  Identified parts").
              (2) VISIBILITY — each section renders only when it actually has
                  rows, replacing three permanent `EmptyState` placeholders that
                  told the user about categories nothing had fallen into. Also
                  design-backed: the source guards every section on a non-empty
                  filtered row count (`showResolveView`/`showIdentifiedView`/
                  `showMatchedView`, `:9500-9502`). */}

      {displayedFiles.length > 0 && (
        <section
          aria-labelledby="staging-needs-resolution-heading"
          className="card staging-section staging-needs-resolution-section"
        >
          <div className="staging-needs-resolution-header">
            <h2 id="staging-needs-resolution-heading" className="staging-section-title">
              {STAGING_SECTION_LABELS.needsResolution}{' '}
              <span
                className="uploads-count-pill staging-needs-resolution-count"
                aria-label={`${displayedFiles.length} item${
                  displayedFiles.length === 1 ? ' needs' : 's need'
                } resolution`}
              >
                {displayedFiles.length}
              </span>
            </h2>
            <p className="caption-text staging-needs-resolution-helper">
              Attach to a part, match a catalog item, or create a new one
            </p>
          </div>
          {/* Human ruling, 2026-09-04 — needs-resolution rows now match on
              FILENAME (a narrow, documented D2 exception — see
              `staging-part-search.ts`'s `matchesFilename`/
              `filterFilesToResolveByQuery`), so this list CAN genuinely come
              up empty on a query that matches no filename here. D4(4) is
              still load-bearing: the section stays on screen with its true
              UNFILTERED count pill above regardless.
              CRITICAL 2 fix (Gate-5 review) — the prior copy claimed "this
              section still shows every item that needs resolution", which
              was FALSE the instant it rendered (it only ever showed when the
              list beneath it was empty). Ordinary no-match copy, matching
              the other three sections' tone, replaces it.
              CRITICAL 3 / SUGGESTION G fix (Gate-5 review) — this is a
              VISUAL note only: the one consolidated `aria-live="polite"`
              region in `StagingToolbar` already announces this same fact
              across every affected section in one sentence — this element
              announcing too would be a second, redundant region firing on
              the same keystroke. Rendered as a plain `<p>`, not an
              `<output aria-live="off">`: `<output>` carries an IMPLICIT
              polite live-region role, so muting it required remembering the
              override every place this pattern was used — the exact class of
              mistake CRITICAL 3 was filed against in the first place, just
              deferred to whichever future edit drops the attribute as
              "redundant". A `<p>` has no live-region role to override. */}
          {hasActiveSearchQuery && searchFilteredFiles.length === 0 && (
            <p className="caption-text staging-search-empty">
              No items here match “{searchQuery.trim()}”.
            </p>
          )}
          <ul className="staging-files-list">
            {searchFilteredFiles.map((file) => (
              <FileToResolveRow
                key={file.fileId}
                file={file}
                identifiedParts={triage.identified}
                onAssign={(targetPartId) =>
                  void runMutation(() =>
                    service.resolveFile(sessionId, file.fileId, 'assign', targetPartId),
                  )
                }
                onCreatePart={() =>
                  void runMutation(() => service.resolveFile(sessionId, file.fileId, 'create'))
                }
                onMatchToCatalog={(catalogId) =>
                  void runMutation(() =>
                    service.resolveFile(sessionId, file.fileId, 'match', undefined, catalogId),
                  )
                }
                onSearchCatalog={(query) =>
                  service.searchStagingCatalog(sessionId, query).then((r) => r.suggestions)
                }
                onKeepAsCustom={() =>
                  void runMutation(() =>
                    service.resolveFile(sessionId, file.fileId, 'keep-as-custom'),
                  )
                }
                onRemove={() =>
                  void runMutation(() => service.resolveFile(sessionId, file.fileId, 'remove'))
                }
                analysisSignal={lookupAnalysisSignal(analysisSignalById, 'unresolved', file.fileId)}
              />
            ))}
          </ul>
        </section>
      )}

      {/*
       * ADR-0142 — the unified "BOM parts" list, replacing (not joining) the
       * "Identified parts"/"Matched to catalog" buckets on a BOM session.
       * `hasBomRows` is the ONE derived boolean, used here and negated below
       * for the legacy buckets — never two disagreeing gates (D12's
       * correction to the UX spec's component-inventory table). Position:
       * right after "Needs resolution", where "Identified parts" sits today
       * (UX-spec §1.2) — NOT a trailing fourth section. No disclosure
       * anywhere: the section body always renders when the section renders
       * (UX-spec §2's "Disclosure: None"), and a group row's only
       * interaction is its selection checkbox (§0/§3.4).
       */}
      {hasBomRows && (
        /* UID-525 — the "Ready to add" BAND IS REMOVED, together with its
           "All parts here move together — no need to select. Check parts
           only to remove them." caption, on a direct product instruction.

           KNOWN DEVIATION FROM THE COMP, stated rather than buried: the
           design renders this band unconditionally directly above the
           section that follows (`Fictiv Platform.dc.html:3128`/`:3146`), and
           it was added to the BOM session for exactly that reason. Removing
           it is a deliberate departure from the design source, not a
           correction toward it.

           The wrapper that used to pair the band with the card
           (`.staging-ready-band-and-bom-parts`, which collapsed
           `.staging-page`'s 24px gap to zero BETWEEN the two while keeping
           it outside the pair) goes with it: with the band gone the card is
           a normal top-level section again and wants that gap back. The
           card's squared top corners are reverted in `uploads.css` for the
           same reason — they existed only so the band could sit flush on
           top of it.

           ALSO REMOVED: the band's `<output aria-live="polite">` carried a
           blocked-state caption ("Resolve the items above to check
           everything out together"). VERIFIED NOT AN A11Y LOSS — the
           blocked state keeps a better-wired announcement of its own in
           `StagingSummaryCard`: an `<output>` reading "N item(s) still
           need(s) resolution", tied to the disabled CTA through
           `aria-describedby`, so the reason is programmatically associated
           with the control it gates and carries an actual count. The band's
           copy was the vaguer, unassociated duplicate of the two. */
        <section
          aria-labelledby="staging-bom-parts-heading"
          className="card staging-section staging-bom-parts-section"
          ref={bomSectionRef}
        >
          <div className="staging-section-toolbar staging-bom-parts-toolbar">
            <h2
              id="staging-bom-parts-heading"
              className="staging-section-title staging-bom-parts-heading"
              // ADR-0157 — the LAST-RESORT focus fallback (no next/previous
              // leaf Remove control, no parent assembly checkbox): a heading
              // is not natively focusable, so `tabIndex={-1}` makes it a
              // valid programmatic focus target without adding it to the
              // regular Tab order.
              tabIndex={-1}
              ref={bomSectionHeadingRef}
            >
              <FolderOutlined aria-hidden="true" />
              <span>{STAGING_SECTION_LABELS.bomParts}</span>{' '}
              <span
                className="uploads-count-pill"
                aria-label={`${bomPartRowCount} part${
                  bomPartRowCount === 1 ? '' : 's'
                } in this bill of materials`}
              >
                {bomPartRowCount}
              </span>
            </h2>
            <p className="caption-text staging-bom-parts-subtitle">
              Structure kept from your bill of materials — swap a match or keep your part
            </p>
          </div>
          {/* Gate-5 C1 — the ONE selection band for the unified section (see
                `bomSelectedCount`'s doc comment). `deleting`/`deleteError`
                combine both underlying per-array flags/messages since a single
                bulk-delete click here can touch identified AND matched rows in
                the same pass. WARNING C fix — `hiddenSelectedCount` was the
                one section missing this wire-up (see
                `bomHiddenSelectedCount`'s doc comment): this is the ONLY
                session type D5's tree pruning runs on, so it is also the one
                band most likely to show a selected count with no rows on
                screen to explain it. */}
          <StagingSelectionBand
            sectionLabel="BOM parts"
            count={bomSelectedCount}
            onClear={() => clearSectionSelection(selectableRowIds)}
            onDeleteSelected={() => void handleDeleteSelectedBom()}
            deleting={identifiedBulkDeleting || matchedBulkDeleting}
            deleteError={
              identifiedBulkDeleteError && matchedBulkDeleteError
                ? `${identifiedBulkDeleteError} ${matchedBulkDeleteError}`
                : (identifiedBulkDeleteError ?? matchedBulkDeleteError)
            }
            hiddenSelectedCount={bomHiddenSelectedCount}
          />
          {/* ADR-0155 D4(3)/D5 — a query that matches none of this section's
                leaves prunes the tree/ungrouped rows to empty (D5) but the
                SECTION stays on screen (its heading/count pill above already
                read the unfiltered `bomPartRowCount`, D3) — this message is
                the distinct empty state for that specific case. CRITICAL 3 /
                SUGGESTION G fix (Gate-5 review) — visual only, and a plain
                `<p>` rather than an `<output aria-live="off">`: the ONE
                consolidated region in `StagingToolbar` already announces this
                (see that component's doc comment), and a `<p>` carries no
                implicit live-region role to override. */}
          {hasActiveSearchQuery && bomVisiblePartRowCount === 0 && (
            <p className="caption-text staging-search-empty">
              No BOM parts match “{searchQuery.trim()}”.
            </p>
          )}
          {/* ADR-0146 D7 — the BOM tree keys its expanded row on the
                  NODE id, not on the SKU-derived `partId`, so selecting a
                  different sourcing option cannot detach the open panel from its
                  row. The FLAT Matched list below still keys on `partId` (those
                  rows have no BOM node), and both share this ONE piece of state
                  so at most one panel is open at a time — unchanged. The two key
                  spaces cannot collide: a `nodeId` is sheet-derived and
                  session-namespaced, a `partId` is a uuid or a `cat:` SKU. ADR-0155
                  D5 — `tree`/`ungrouped` are the SEARCH-FILTERED copy; every other
                  prop here (`selectedIdentified`, etc.) is unaffected by search. */}
          <StagingBomPartsTree
            tree={searchFilteredUnifiedBomRows.tree}
            ungrouped={searchFilteredUnifiedBomRows.ungrouped}
            // UID-498 — root header title source; see that prop's own doc
            // comment on `StagingBomPartsTreeProps`. `exactOptionalPropertyTypes`
            // rejects an explicit `undefined` for an optional prop, so only
            // spread the key in when `triage.sessionName` is actually present.
            {...(triage.sessionName !== undefined ? { sessionName: triage.sessionName } : {})}
            selectedIdentified={selectedStaged}
            onToggleSelectIdentified={(partId, shiftKey) => toggleStagedSelection(partId, shiftKey)}
            // UID-497 — the session's DRAWING POOL, which is a superset of
            // the drawing-shaped files-to-resolve rows and a subset of the
            // list itself (a `.step` model missing its drawing is not a
            // drawing). The identified arm's `onAssociateDrawing` below
            // still resolves a real `fileId`, and every pool entry that came
            // from a file row keeps its own id, so that path is unchanged.
            candidateDrawings={drawingPool}
            onAssociateDrawing={(partId, fileId) =>
              void runMutation(() => service.associateDrawing(sessionId, partId, fileId))
            }
            // UID-554 fix — was a bare `service.generateDrawing(...)` call:
            // it reached the server fine, but nothing here ever wrote the
            // returned triage into `state`, so a real success (now possible
            // for a `type === 'custom'` identified part) rendered nothing
            // until an UNRELATED mutation elsewhere refetched. Routed through
            // `applyMutation` (NOT `runMutation` — that swallows the
            // rejection into page-level `mutationError`, breaking
            // `DrawingAssociationControl`'s own inline error message on a
            // genuine 501 for a non-custom part); `applyMutation` still
            // rethrows on failure, so that per-control error handling is
            // unchanged.
            onGenerateDrawing={(partId) =>
              applyMutation(() => service.generateDrawing(sessionId, partId))
            }
            onUploadDrawing={(partId, file) =>
              runMutation(() => service.uploadDrawing(sessionId, partId, file))
            }
            // Gate-5 fix — NOT `void`-ed: `QuantityInput` awaits this
            // boolean to know whether to revert its draft on a rejected
            // edit (D4). `runMutation` still surfaces the error itself via
            // `mutationError`, unchanged.
            onQuantityChange={(partId, quantity) =>
              runMutation(() => service.updateQuantity(sessionId, partId, quantity))
            }
            // ADR-0156 D1/D4 — the matched/catalog/unresolved arms' own
            // node-scoped verb, keyed on `nodeId` (never `partId`, same
            // identity hazard `onSelectCatalogOption` below guards
            // against). Server-authoritative, no optimistic update — a
            // 409 (e.g. an assembly node) surfaces via `runMutation`'s
            // existing error path, and (Gate-5 fix) the returned `false`
            // is also how the cell's own draft reverts instead of sticking.
            onNodeQuantityChange={(nodeId, quantity) =>
              runMutation(() => service.updateNodeQuantity(sessionId, nodeId, quantity))
            }
            onMaterialChange={(partId, material) =>
              void runMutation(() => service.updateMaterial(sessionId, partId, material))
            }
            // UID-497 (T10) — the CUSTOM ("Make") BOM leaf's own drawing and
            // material verbs, node-scoped. The `partId`-scoped versions
            // above are unreachable for these rows: a custom BOM line has no
            // `staged_file` row, so `associateDrawing`/`updateMaterial`
            // would 404 (the same reason `onNodeQuantityChange` exists
            // alongside `onQuantityChange`).
            //
            // The DRAWING POOL, not `triage.filesToResolve`: the menu offers
            // the parts' own drawings, orphan drawings from loose files, and
            // other leaves' drawings — see `buildSessionDrawingPool`, which
            // mirrors the set the server will actually accept.
            onAssociateNodeDrawing={(nodeId, filename) =>
              void runMutation(() => service.associateNodeDrawing(sessionId, nodeId, filename))
            }
            onDetachNodeDrawing={(nodeId) =>
              void runMutation(() => service.detachNodeDrawing(sessionId, nodeId))
            }
            // Scope (b) — the toast and the "Generating…" chip are the
            // control's own (it shows them while this promise is pending);
            // ALL this wiring adds is the demo wait, so the chip is visible
            // for a moment before the filename appears. The delay lives in
            // ONE named constant (`DEMO_DRAWING_GENERATE_DELAY_MS`) so it
            // can be tuned or removed without hunting, and it is CLIENT-side
            // because the server's `generate` arm attaches immediately —
            // making a real endpoint sleep would be the wrong place for a
            // presentation affordance.
            onGenerateNodeDrawing={async (nodeId) => {
              await new Promise((resolve) => setTimeout(resolve, DEMO_DRAWING_GENERATE_DELAY_MS))
              return runMutation(() => service.generateNodeDrawing(sessionId, nodeId))
            }}
            // Through `runMutation`, matching the shipped part-scoped
            // `onUploadDrawing` above: a refused upload (a non-PDF is a 415)
            // surfaces in the page-level alert, which is where every other
            // staging write's failure already appears. Deliberately not the
            // control's inline message — one error surface per page beats
            // two that can disagree.
            onUploadNodeDrawing={(nodeId, file) =>
              runMutation(() => service.uploadNodeDrawing(sessionId, nodeId, file))
            }
            onNodeMaterialChange={(nodeId, material) =>
              void runMutation(() => service.updateNodeMaterial(sessionId, nodeId, material))
            }
            onRemoveIdentified={(partId) =>
              void runMutation(() => service.removeStagedPart(sessionId, partId))
            }
            selectedMatched={selectedStaged}
            onToggleSelectMatched={(partId, shiftKey) => toggleStagedSelection(partId, shiftKey)}
            expandedRowKey={expandedMatchedId}
            onToggleExpandRow={(rowKey: string) =>
              setExpandedMatchedId((prev) => (prev === rowKey ? null : rowKey))
            }
            onIgnore={(partId) => void runMutation(() => service.ignoreMatch(sessionId, partId))}
            onUndo={(partId) => void runMutation(() => service.confirmMatch(sessionId, partId))}
            onSelectOption={(partId, optionId) =>
              void runMutation(() => service.swapSourcing(sessionId, partId, optionId))
            }
            // ADR-0146 D11 step 5 — the catalog-leaf sourcing dropdown's OWN
            // write path (node-subject, not `swapSourcing`'s `partId`-scoped
            // one, which a catalog leaf has no `staged_file` row for).
            onSelectCatalogOption={(nodeId, optionId) => {
              // ADR-0162 D5 / UID-496 (f) — the design's own confirmation
              // toast (`Fictiv Platform.dc.html:9420`: `flash('Swapped to ' +
              // (label || 'catalog part'))`). The vendor is resolved from the
              // CURRENT triage BEFORE the mutation, because the write returns
              // a refreshed triage in which `optionId` may no longer be the
              // one we are naming — and a toast that names the wrong vendor
              // on a money-adjacent surface is worse than no toast.
              //
              // Falls back to the design's own `'catalog part'` rather than
              // inventing wording or skipping the toast, so a lookup miss
              // degrades to a true-but-vague message instead of a false one.
              const vendor = catalogOptionVendor(triage, optionId)
              void runMutation(async () => {
                const next = await service.selectCatalogNodeSourcingOption(
                  sessionId,
                  nodeId,
                  optionId,
                )
                showToast(`Swapped to ${vendor}`, 'success')
                return next
              })
            }}
            onKeepCatalogNode={(nodeId, name) =>
              void runMutation(async () => {
                const next = await service.keepCatalogNodeBomReference(sessionId, nodeId)
                /* THE DESIGN'S LITERAL (`dc.html:6258`), per the 2026-09-09
                     human ruling recorded at `MatchedSourcingPanel`'s
                     `KEEP_ROW_TITLE`, which reversed ADR-0146 Am.1 D12.
                     This toast moves WITH the row label by the old comment's
                     own logic: it argued D4 "binds a TOAST as much as a row
                     label — it is user-visible text like any other", and that
                     reasoning is sound in both directions. Having overruled D4
                     for the label, leaving the toast on the superseded
                     catalog-accurate wording would split one ruling across two
                     strings the user sees seconds apart. */
                showToast(`Keeping your uploaded ${name}`, 'success')
                return next
              })
            }
            // ADR-0162 D3.2 — no toast on either ignore arm, matching the
            // design, whose `ignoreMatch` (`:6259`) flashes nothing. The state
            // change is self-evident on the row (an `Ignored` chip replaces the
            // match cell, with an Undo beside it), so a toast would be noise —
            // and inventing one here would be inventing copy.
            onIgnoreCatalogNode={(nodeId, name, occurrenceCount) =>
              void runMutation(async () => {
                const next = await service.ignoreCatalogNodeMatch(sessionId, nodeId)
                // Gate-5 W7 — ANNOUNCE. The chosen posture of "no toast, the
                // row speaks for itself" is true visually and false for
                // assistive tech: nothing was announced at all. Uses the SAME
                // page-level `aria-live` region ADR-0157's Remove already
                // established one verb over, rather than a second mechanism.
                //
                // NO FOCUS RESTORE, and that is a MEASURED decision, not an
                // omission. Gate-5 reasoned focus would fall to
                // `document.body` because the Ignore button unmounts. It does
                // not: React reconciles the two branches (each renders a
                // `<button>` in the same slot), reuses the DOM node, and focus
                // persists. A `pendingFocusTarget` was added on that reasoning
                // and removed once a mutation test proved deleting it changed
                // nothing — dead code carrying a false claim. The real
                // behaviour is characterized in the W7 test.
                //
                // The count is named for the same reason Remove names it: a
                // decision on a deduped row applies to every position it
                // represents (Gate-5 C3's fan-out), so announcing only the
                // row would understate what just happened.
                setLiveAnnouncement(
                  occurrenceCount > 1
                    ? `Ignored the catalog match for “${name}” at all ${occurrenceCount} positions in this BOM`
                    : `Ignored the catalog match for “${name}”`,
                )
                return next
              })
            }
            onUndoIgnoreCatalogNode={(nodeId, name, occurrenceCount) =>
              void runMutation(async () => {
                const next = await service.unignoreCatalogNodeMatch(sessionId, nodeId)
                setLiveAnnouncement(
                  occurrenceCount > 1
                    ? `Restored the catalog match for “${name}” at all ${occurrenceCount} positions in this BOM`
                    : `Restored the catalog match for “${name}”`,
                )
                return next
              })
            }
            // ADR-0146 Am.1 D12/D14 — the `keep` arm of that same
            // node-subject route. NOT `ignoreMatch` (which is what
            // `onKeepUploaded` below maps to for a file-backed leaf): this
            // writes the durable `'kept-bom-reference'` decision, so the
            // choice survives a reload instead of being modelled as an
            // ignore.
            onKeepUploaded={(partId) =>
              void runMutation(() => service.ignoreMatch(sessionId, partId))
            }
            onRemoveMatched={(partId) =>
              void runMutation(() => service.removeStagedPart(sessionId, partId))
            }
            onRemoveCatalogNode={(nodeId, name, occurrenceCount) =>
              void handleRemoveCatalogNode(nodeId, name, occurrenceCount)
            }
            onDismissNode={(nodeId, name, bomLine, siblingIndex) =>
              void handleDismissNode(nodeId, name, bomLine, siblingIndex)
            }
            onUndoDismissNode={handleUndoDismissNode}
          />
        </section>
      )}

      {/* ADR-0142 — "Identified parts"/"Matched to catalog" render EXACTLY as
          they do today, UNCHANGED, whenever `!hasBomRows` — the dominant,
          permanent case (a CAD-only session, or any hierarchy that resolves
          to zero part rows). On a BOM session they render NOT AT ALL: the
          unified section above already carries every one of their rows
          (D2's flat-array-driven population), so rendering both would be
          the one-state-rendered-twice fragility ADR-0136 D1's additive IA
          had, which this ADR exists to dissolve. */}
      {!hasBomRows && displayedIdentified.length > 0 && (
        <section
          aria-labelledby="staging-identified-heading"
          // UID-600 — `.staging-identified-section` (uploads.css) strips the
          // `.card` padding that used to inset the table below (see that
          // class's own comment for why the swap to a real `border` is
          // required, not just dropping `padding`), so the table can span
          // this card's full width instead of sitting inset inside it.
          className="card staging-section staging-identified-section"
        >
          <div className="staging-section-toolbar">
            <div>
              <h2 id="staging-identified-heading" className="staging-section-title">
                {STAGING_SECTION_LABELS.identified}{' '}
                <span
                  className="uploads-count-pill"
                  aria-label={`${displayedIdentified.length} identified part${
                    displayedIdentified.length === 1 ? '' : 's'
                  }`}
                >
                  {displayedIdentified.length}
                </span>
              </h2>
              {/* UID-402 / ADR-0130 (UID-425 Bug 2) — was two stacked `<p>`s
                  (this line, plus the design's "All parts here move
                  together..." caption verbatim from
                  `Fictiv Platform.dc.html:3134`); merged into one line since
                  both are block-level and always wrapped onto separate
                  lines regardless of viewport width (visual defect fix). */}
              <p className="caption-text staging-ready-identified-subtitle">
                Review the drawing, type, and quantity for each part. All parts here move together —
                no need to select. Check parts only to remove them.
              </p>
            </div>
          </div>
          {/* PM ruling (2026-09-01) — the design's per-section selection band
              (`Fictiv Platform.dc.html:3136-3143`), welded directly under
              the section header (border-top:none). Scoped to identified rows
              only. */}
          <StagingSelectionBand
            sectionLabel="identified parts"
            count={identifiedSelectedCount}
            onClear={() => clearSectionSelection(identifiedIds)}
            onDeleteSelected={() => void handleDeleteSelectedIdentified()}
            deleting={identifiedBulkDeleting}
            deleteError={identifiedBulkDeleteError}
            hiddenSelectedCount={identifiedHiddenSelectedCount}
          />
          <table className="parts-table staging-ready-table">
            <thead>
              <tr>
                <th scope="col">
                  {/* PM ruling (2026-09-01) — scoped to THIS section's rows
                      only; the accessible name NAMES the section so two
                      select-alls on one page are distinguishable to a
                      screen reader. CRITICAL 1 fix (Gate-5 review / security)
                      — both the tri-state AND the toggle target now read
                      `identifiedVisibleIds` (query-aware), never
                      `identifiedIds` (unfiltered): before this fix, ticking
                      "select all" under an active query silently added
                      every HIDDEN identified row to `selectedStaged` too, so
                      a subsequent "Delete selected" — which correctly acts
                      on the user's FULL true selection — deleted rows the
                      user never saw and never chose. See
                      `identifiedVisibleIds`'s own doc comment.

                      UID-600 — migrated from a raw, unwrapped
                      `<input type="checkbox">` to the shared `Checkbox`
                      primitive (`components/ui/Checkbox.tsx`), which this
                      accessible-name-only control now supplies via a
                      visually-hidden (`sr-only`) label instead of
                      `aria-label`, and which owns the `.indeterminate` DOM
                      property assignment `Checkbox` was extended to support
                      (this header previously set `aria-checked="mixed"`
                      with no matching `.indeterminate` — semantically
                      tri-state but visually indistinguishable from
                      unchecked, since the shared `.checkbox-input` paint
                      has no `:indeterminate` rule of its own and nothing
                      here ever assigned the DOM property). */}
                  <Checkbox
                    className="staging-select-all-checkbox"
                    checked={identifiedAllSelected}
                    indeterminate={identifiedSomeSelected && !identifiedAllSelected}
                    onChange={() => toggleSelectAllFor(identifiedVisibleIds, identifiedAllSelected)}
                    label={<span className="sr-only">Select all identified parts</span>}
                  />
                </th>
                <th scope="col">
                  <span className="sr-only">Thumbnail</span>
                </th>
                <th scope="col">Part name</th>
                {/* ADR-0130 (Bug 4 item 4) — the design's TRIAGE-LIST header
                    (`Fictiv Platform.dc.html:3274`) is "Drawing / matched
                    parts", not "Catalog match / drawing" (that label belongs
                    to the BOM-session header at `:3158`). */}
                <th scope="col">Drawing / matched parts</th>
                <th scope="col">Part type</th>
                <th scope="col">Configuration status</th>
                <th scope="col">Qty</th>
                <th scope="col">
                  <span className="sr-only">Remove</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {/* ADR-0155 D4(3) — the distinct "nothing matches" empty state
                  for this section; the section itself stays on screen (its
                  heading/count above already read `displayedIdentified`,
                  unfiltered, D3). CRITICAL 3 / SUGGESTION G fix (Gate-5
                  review) — visual only, and a plain `<p>` rather than an
                  `<output aria-live="off">`: the ONE consolidated region in
                  `StagingToolbar` already announces this, and a `<p>` has no
                  implicit live-region role to override. */}
              {hasActiveSearchQuery && searchFilteredIdentified.length === 0 && (
                <tr>
                  <td colSpan={8}>
                    <p className="caption-text staging-search-empty">
                      No identified parts match “{searchQuery.trim()}”.
                    </p>
                  </td>
                </tr>
              )}
              {searchFilteredIdentified.map((part) => (
                <IdentifiedPartRow
                  key={part.partId}
                  part={part}
                  // UID-554 fix (code-review W3) — was `triage.filesToResolve`,
                  // which is only the orphan/duplicate/no-match rows still
                  // AWAITING resolution. The BOM-tree call site above already
                  // uses the full session drawing pool so a `type: 'custom'`
                  // part can reuse a drawing already attached elsewhere; this
                  // flat table's identical row kind (an identified custom
                  // part with no BOM node) needs the SAME pool, or the
                  // server's new fallback (`applyAssignDrawing`) is
                  // unreachable from here and the menu keeps offering
                  // `.step`/`no-match`/`duplicate` rows the server 404s on.
                  candidateDrawings={drawingPool}
                  onAssociateDrawing={(fileId) =>
                    void runMutation(() => service.associateDrawing(sessionId, part.partId, fileId))
                  }
                  // UID-554 fix — same `applyMutation` (not `runMutation`)
                  // wiring as the BOM-tree call site above; see that comment.
                  onGenerateDrawing={() =>
                    applyMutation(() => service.generateDrawing(sessionId, part.partId))
                  }
                  onUploadDrawing={(file) =>
                    runMutation(() => service.uploadDrawing(sessionId, part.partId, file))
                  }
                  onQuantityChange={(quantity) =>
                    void runMutation(() => service.updateQuantity(sessionId, part.partId, quantity))
                  }
                  onRemove={() =>
                    void runMutation(() => service.removeStagedPart(sessionId, part.partId))
                  }
                  selected={selectedStaged.has(part.partId)}
                  onToggleSelect={(event) => toggleStagedSelection(part.partId, event.shiftKey)}
                  analysisSignal={lookupAnalysisSignal(
                    analysisSignalById,
                    'identified',
                    part.partId,
                  )}
                />
              ))}
            </tbody>
          </table>
        </section>
      )}

      {!hasBomRows && displayedMatched.length > 0 && (
        <section aria-labelledby="staging-matched-heading" className="card staging-section">
          <div className="staging-section-toolbar">
            {/* PM ruling (2026-09-01) — the matched section has no `<table>`/
                `<thead>` (it's a `<ul>`), so its own select-all lives in this
                header toolbar rather than a header row. Scoped to this
                section's non-ignored rows only — see the identified select-all's
                doc comment above for the full "one select-all per section"
                rationale, and its CRITICAL 1 fix comment for why this reads
                `matchedVisibleSelectableIds`, not `matchedSelectableIds`.

                UID-600 — migrated to the shared `Checkbox` primitive; see
                the identified select-all's doc comment above for why
                (`.indeterminate` DOM-property support added to `Checkbox`
                for this ticket). */}
            <Checkbox
              className="staging-select-all-checkbox"
              checked={matchedAllSelected}
              indeterminate={matchedSomeSelected && !matchedAllSelected}
              onChange={() => toggleSelectAllFor(matchedVisibleSelectableIds, matchedAllSelected)}
              label={<span className="sr-only">Select all matched parts</span>}
            />
            <div>
              <h2 id="staging-matched-heading" className="staging-section-title">
                {STAGING_SECTION_LABELS.matched}{' '}
                <span
                  className="uploads-count-pill"
                  aria-label={`${displayedMatched.length} part${
                    displayedMatched.length === 1 ? '' : 's'
                  } matched to catalog`}
                >
                  {displayedMatched.length}
                </span>
              </h2>
              <p className="caption-text staging-ready-matched-subtitle">
                Swap the catalog source or ignore the match
              </p>
            </div>
          </div>
          {/* PM ruling (2026-09-01) — same per-section selection band as the
              identified section above, scoped to matched rows only. */}
          <StagingSelectionBand
            sectionLabel="matched parts"
            count={matchedSelectedCount}
            onClear={() => clearSectionSelection(matchedSelectableIds)}
            onDeleteSelected={() => void handleDeleteSelectedMatched()}
            deleting={matchedBulkDeleting}
            deleteError={matchedBulkDeleteError}
            hiddenSelectedCount={matchedHiddenSelectedCount}
          />
          {/* ADR-0155 D4(3) — same "nothing matches" empty state as the
              identified section; heading/count above stay unfiltered (D3).
              CRITICAL 3 / SUGGESTION G fix (Gate-5 review) — visual only,
              and a plain `<p>` rather than an `<output aria-live="off">`:
              the ONE consolidated region in `StagingToolbar` already
              announces this, and a `<p>` has no implicit live-region role to
              override. */}
          {hasActiveSearchQuery && searchFilteredMatched.length === 0 && (
            <p className="caption-text staging-search-empty">
              No matched parts match “{searchQuery.trim()}”.
            </p>
          )}
          <ul className="staging-matched-list">
            {searchFilteredMatched.map((part) => (
              <MatchedPartRow
                key={part.partId}
                part={part}
                selected={selectedStaged.has(part.partId)}
                onToggleSelect={(event) => toggleStagedSelection(part.partId, event.shiftKey)}
                expanded={expandedMatchedId === part.partId}
                onToggleExpand={() =>
                  setExpandedMatchedId((prev) => (prev === part.partId ? null : part.partId))
                }
                onIgnore={() => void runMutation(() => service.ignoreMatch(sessionId, part.partId))}
                onUndo={() => void runMutation(() => service.confirmMatch(sessionId, part.partId))}
                onSelectOption={(optionId) =>
                  void runMutation(() => service.swapSourcing(sessionId, part.partId, optionId))
                }
                onKeepUploaded={() =>
                  void runMutation(() => service.ignoreMatch(sessionId, part.partId))
                }
                onRemove={() =>
                  void runMutation(() => service.removeStagedPart(sessionId, part.partId))
                }
                analysisSignal={lookupAnalysisSignal(analysisSignalById, 'matched', part.partId)}
              />
            ))}
          </ul>
        </section>
      )}

      {/* UID-402 — a `BulkConfigureModal` was rendered here, opened by a
          "Configure selected" button in the Identified-parts section header.
          BOTH are removed: Triage is review/resolve only, so configuration must
          not be reachable from this page. This is design-backed — the canonical
          source's only bulk-Configure affordance lives on the RFQ draft screen
          (`Fictiv Platform.dc.html:4157`, `rfqDraftView.onBulkConfigure`), not
          on Triage. Configuration remains available to the user on the three
          surfaces that own it: the Part Library
          (`PartsBulkConfigureModal`/`EditConfigurationModal`) and the RFQ
          compose screen (`ConfigurePartsModal`). See ADR-0124.

          UID-584 — the modal COMPONENT itself is now deleted too, not just its
          affordance. UID-402 removed the render site and the trigger but left
          `uploads/components/BulkConfigureModal.tsx` on disk with zero
          importers, where it drifted: it carried its own hardcoded
          `PROCESSES`/`MATERIALS`/`FINISHES` lists while the three live surfaces
          above moved to the catalog-backed cascade. Deleted rather than left
          unreachable, so it cannot be mistaken for a live surface or
          resurrected as a shortcut past ADR-0124 D1. */}

      {/* Product decision (UID-588, 2026-09-10) — the "Add these parts to your
          library?" confirmation dialog on "Review in library" is removed:
          the click now proceeds directly to the real `confirmParts` write
          (`handleReviewInLibrary` -> `handleReviewInLibraryConfirmed`) with no
          intermediate prompt. A failure still surfaces inline, beside the
          hand-off button, via `handoffError` on `StagingSummaryCard` — the
          same Gate-5 W5 pattern the RFQ CTA already uses, just without the
          modal. */}

      {/* Product decision (2026-09-03) — the "Create this RFQ?" confirmation
          dialog is removed: "Create RFQ" now proceeds directly to the real
          `confirmParts` write (`handleCreateRfq` -> `handleCreateRfqConfirmed`)
          with no intermediate prompt. A failure still surfaces inline, beside
          the primary CTA, via `rfqHandoffError` on `StagingSummaryCard` — the
          same Gate-5 W5 pattern, just without the modal. */}
    </section>
  )
}

/**
 * ADR-0157 D2 — a small recursive lookup of one BOM node's `dismissedAt` in
 * a FRESHLY RETURNED `bomHierarchy` (never the page's own memoized tree,
 * which is a render behind by the time the mutation resolves). `null` for
 * both "absent" (hard removed) and "present but never dismissed" — the two
 * are deliberately not distinguished here; the caller only needs to know
 * whether a still-present node carries a dismissal.
 */
/**
 * ADR-0162 D5 / UID-496 (f) — the vendor label for the Select toast, resolved
 * off the CURRENT triage by `optionId`.
 *
 * Scans every matched row rather than taking a `partId`, deliberately: a
 * catalog leaf's `partId` is minted from its SKU and CHURNS on selection
 * (ADR-0146 D7), so keying this lookup on it would go stale at exactly the
 * moment the user acts. `optionId` is itself minted from the option's SKU
 * (`catalogItemToSourcingOption`), so it is stable across the write.
 *
 * Returns the design's own fallback `'catalog part'`
 * (`Fictiv Platform.dc.html:9420`) on a miss, so an unresolved lookup degrades
 * to a true-but-vague toast rather than a false one naming the wrong vendor.
 */
function catalogOptionVendor(triage: StagingTriage | null, optionId: string): string {
  for (const match of triage?.matched ?? []) {
    const option = match.sourcingOptions.find((o) => o.optionId === optionId)
    if (option) {
      return option.vendor.trim() === '' ? 'catalog part' : option.vendor
    }
  }
  return 'catalog part'
}

function findDismissedAt(nodes: readonly StagingBomNode[], nodeId: string): string | null {
  for (const node of nodes) {
    if (node.nodeId === nodeId) return node.dismissedAt ?? null
    const found = findDismissedAt(node.children, nodeId)
    if (found !== null) return found
  }
  return null
}
